import { useState, useEffect } from "react";
import { GuarantorCompaniesFinder } from "@/pages/legal/GuarantorCompaniesFinder";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WizardDebugButton } from "@/components/ReportBugDialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import UseOfFundsForm from "./UseOfFundsForm";
import MondayDealSearch from "./MondayDealSearch";
import DocsOutTrackingSection from "./DocsOutTrackingSection";
import BrokerOnboarding from "./BrokerOnboarding";
import SumsubMatchDialog from "./SumsubMatchDialog";
import { DocuSignEnvelopesPanel } from "./DocuSignEnvelopesPanel";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";
import { 
  FileText, Plus, Search, Building2, User, CheckCircle, Clock, 
  AlertCircle, ChevronRight, ChevronLeft, Download, Eye, Loader2,
  Bell, RefreshCw, MapPin, XCircle, Send, Mail, UserCheck, Pencil, Fingerprint,
  ArrowUp, ArrowDown, Upload, AlertTriangle
} from "lucide-react";
import api, { postcodeAddresses, contractsDocInline, contractsDocAsWord } from "@/lib/api";
import { classifyBlob, openBlobInNewTab } from "@/lib/safeBlob";
import { useAuth } from "@/contexts/AuthContext";

// SimplyFunded default company details for contracts
const SF_DEFAULT_ADDRESS = "S10.B Evolve Business Centre, Cygnet Way, Houghton Le Spring, Durham, DH45QY";
const SF_DEFAULT_PHONE = "+44 1642 057 445";

// One-click "Use Sumsub info" — searches the UK workspace by name and fills
// the signer's contact details from their verified ID check.
const SumsubUseButton = ({ name, onFill, testid }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!name) return;
    try {
      setBusy(true);
      const { data } = await api.get("/contracts/sumsub/search", { params: { q: name } });
      const list = data.applicants || [];
      if (!list.length) { toast.info(`No Sumsub ID check found for ${name}`); return; }
      const best = list.find((a) => a.review_answer === "GREEN") || list[0];
      onFill(best);
      toast.success(`Sumsub ${best.review_answer || "check"} — ${best.full_name}: details filled`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sumsub lookup failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button type="button" variant="outline" size="sm" className="h-6 text-[11px] mt-1 px-2" onClick={run}
      disabled={busy} data-testid={testid}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Fingerprint className="w-3 h-3 mr-1 text-blue-600" />}
      Use Sumsub info
    </Button>
  );
};

// Dropdown of preloaded company witnesses (managed in Portal Settings → Company Witnesses)
function WitnessSelect({ witnesses, onPick, testId }) {
  if (!witnesses || witnesses.length === 0) return null;
  return (
    <div className="mb-2">
      <Label className="text-xs text-muted-foreground">Preloaded Company Witness</Label>
      <Select onValueChange={(v) => {
        const w = witnesses[parseInt(v, 10)];
        if (w) onPick(w);
      }}>
        <SelectTrigger className="mt-1 h-9 bg-white" data-testid={testId}>
          <SelectValue placeholder="Pick a company witness (or enter manually below)..." />
        </SelectTrigger>
        <SelectContent>
          {witnesses.map((w, i) => (
            <SelectItem key={i} value={String(i)}>
              {w.name}{w.occupation ? ` — ${w.occupation}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function DocumentGeneration({ wizardOnly = false, wizardDeal = null, onWizardClose = null }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedDeal = wizardDeal || searchParams.get("deal");
  const { user } = useAuth();
  
  // Top-level onboarding mode — "customer" (default, existing flow) or "broker" (new broker engagement flow)
  const [onboardingMode, setOnboardingMode] = useState("customer");
  
  // Requests state
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  
  // In-progress contracts (envelopes created but not sent)
  const [inProgressContracts, setInProgressContracts] = useState([]);
  
  // Completed contracts
  const [completedContracts, setCompletedContracts] = useState({ contracts_tab: [], legal_tab: [] });
  const [loadingCompleted, setLoadingCompleted] = useState(true);
  
  // All envelopes for archive search
  const [allEnvelopes, setAllEnvelopes] = useState([]);
  const [envelopeSearchTerm, setEnvelopeSearchTerm] = useState("");
  const [loadingEnvelopes, setLoadingEnvelopes] = useState(false);
  const [envelopesError, setEnvelopesError] = useState(null);
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  
  // Document generation dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Form state
  const [contractTypes, setContractTypes] = useState({
    ftla: true, // Always required
    debenture: false,
    guaranteeType: "pg", // "pg" or "pg_cross_company"
    useOfFunds: false, // Top-up / consolidation flag — auto-detected from "(TU)" in app name
  });

  // Use of Funds disbursement breakdown (filled in Step 6 before generation)
  const [useOfFundsBreakdown, setUseOfFundsBreakdown] = useState({
    mode: "topup", // "topup" | "consolidation" | "both"
    existingSfNumber: "",
    existingSfBalance: 0,
    lenders: [{ name: "", amount: 0 }], // Dynamic list, up to 10
    selectedLoandiskLoanId: null,
  });

  // Tracks whether the Use of Funds form was auto-populated from the
  // application record so the operator can see what was pre-filled.
  const [useOfFundsPrefill, setUseOfFundsPrefill] = useState(null);

  // Jotform OfferFormID captured from Monday (column text_mm3bh12w).
  // Phase 2 — used for offer-form attachment via Jotform API.
  const [offerFormId, setOfferFormId] = useState("");

  // URL to the signed Jotform PDF proposal pushed onto Monday by make.com
  // (column text_mm3bbn4p). When present we attach this PDF to the envelope
  // so the customer always sees the offer they were proposed.
  const [offerPdfUrl, setOfferPdfUrl] = useState("");
  const [attachOfferPdf, setAttachOfferPdf] = useState(true);
  const [attachOfferForm, setAttachOfferForm] = useState(false);
  // Monday item id of the selected deal — lets us fetch a FRESH offer-file URL
  // (the stored S3 link expires) for both View Offer and the envelope attach.
  const [mondayItemId, setMondayItemId] = useState(null);
  const [offerViewUrl, setOfferViewUrl] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  
  // Loan details state
  const [loanDetails, setLoanDetails] = useState({
    loanAmount: 0,
    loanTermWeeks: 26,
    factorRate: 1.5, // Factor rate with default 1.5
    paymentFrequency: "daily",
    paymentDay: "Tuesday", // Day of week for weekly payments
    firstPaymentDate: "", // ISO date — "commencing on" date in the docs (blank = today)
    adminFee: 0, // Arrangement/admin fee from Monday.com
    legalFee: 0, // Legal fee
    sfNumber: "", // SF number for manual entry
    customerPhone: "" // Customer telephone
  });
  
  // Track if this is a sales request (should lock fields)
  const [isFromSalesRequest, setIsFromSalesRequest] = useState(false);
  // Fields whose value came from the offer PDF (not Monday / manual entry)
  const [offerSourced, setOfferSourced] = useState({});
  
  // Manual override states - allows editing locked fields with friction
  const [manualOverrides, setManualOverrides] = useState({
    loanAmount: false,
    loanTermWeeks: false,
    factorRate: false,
    paymentFrequency: false,
    adminFee: false,
    legalFee: false,
    company: false,
    bankDetails: false
  });
  
  // Bank details state (for FTLA)
  const [bankDetails, setBankDetails] = useState({
    accountNumber: "",
    sortCode: "",
    accountName: "",
    bankName: "",
    verified: false,
    verifiedBy: null,
    verifiedAt: null
  });
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);
  const [bankDetailsSource, setBankDetailsSource] = useState(null); // "verified", "application", "manual"
  
  // UK Address lookup state - using getaddress.io
  const [addressSearchQuery, setAddressSearchQuery] = useState("");
  const [addressSearchResults, setAddressSearchResults] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [activeAddressDirector, setActiveAddressDirector] = useState(null);
  
  // Company search state
  const [companySearch, setCompanySearch] = useState("");
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [searchingCompany, setSearchingCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loadingCompanyDetails, setLoadingCompanyDetails] = useState(false);
  
  // Director state
  const [directors, setDirectors] = useState([]);
  const [signingDirectors, setSigningDirectors] = useState([]); // Director names signing on behalf of company (max 6 — UoF/Debenture loop; FTLA prints the first 3)
  const [pgDirectors, setPgDirectors] = useState([]); // Directors requiring PG
  const [directorAddresses, setDirectorAddresses] = useState({}); // {directorName: address}
  const [directorPhones, setDirectorPhones] = useState({}); // {directorName: phone}
  const [directorEmails, setDirectorEmails] = useState({}); // {directorName: email}
  const [directorDobDays, setDirectorDobDays] = useState({}); // {directorName: "DD"} — manual day fill since Companies House redacts the day
  
  // Derived: primary signing director (position 0 of signingDirectors array) — 
  // keeps existing Step 5 single-signer witness/email config working unchanged for the primary signer.
  const signingDirector = signingDirectors[0] || null;

  // Sumsub ID-check matching (UK workspace)
  const [sumsubDialogDirector, setSumsubDialogDirector] = useState(null);
  const [sumsubMatches, setSumsubMatches] = useState({}); // {directorName: applicant}

  const applySumsubMatch = (directorName, applicant) => {
    if (!directorName || !applicant) return;
    if (applicant.address) setDirectorAddresses(prev => ({ ...prev, [directorName]: applicant.address }));
    if (applicant.phone) setDirectorPhones(prev => ({ ...prev, [directorName]: applicant.phone }));
    if (applicant.email) setDirectorEmails(prev => ({ ...prev, [directorName]: applicant.email }));
    if (applicant.dob) {
      const day = (applicant.dob.split("-")[2] || "").replace(/^0/, "");
      if (day) setDirectorDobDays(prev => ({ ...prev, [directorName]: day }));
    }
    setSumsubMatches(prev => ({ ...prev, [directorName]: applicant }));
    setSumsubDialogDirector(null);
    toast.success(`Matched ${directorName} to Sumsub check${applicant.review_answer ? ` (${applicant.review_answer})` : ""}`);
  };
  
  // Cross company state - now supports multiple (up to 6)
  const [crossCompanySearch, setCrossCompanySearch] = useState("");
  const [crossCompanyResults, setCrossCompanyResults] = useState([]);
  const [searchingCrossCompany, setSearchingCrossCompany] = useState(false);
  const [selectedCrossCompany, setSelectedCrossCompany] = useState(null);
  const [crossCompanyDetails, setCrossCompanyDetails] = useState(null);
  const [crossCompanySigningDirector, setCrossCompanySigningDirector] = useState(null);
  // Multiple cross companies array (up to 6)
  const [crossCompanies, setCrossCompanies] = useState([]);
  // { company_number, company_name, registered_address, signing_director_name, officer_names[] }
  // PG ↔ cross-company links: { [guarantorName]: Set-like array of company_numbers }.
  // Auto-seeded from Companies House officers; manual PGs start un-ticked; user can toggle.
  const [pgCompanyLinks, setPgCompanyLinks] = useState({});
  const [pgLinkTouched, setPgLinkTouched] = useState({}); // `${name}|${company_number}` → true once user toggled
  
  // Manual PG guarantors (beyond directors) - up to 6 PGs total
  const [manualPgGuarantors, setManualPgGuarantors] = useState([]);
  // Known-director advisory (LoanDisk cross-check on CH search — Aug 2026)
  const [knownDirectorMatches, setKnownDirectorMatches] = useState([]);

  const runDirectorCrosscheck = async (names) => {
    const clean = (names || []).map(n => (n || "").trim()).filter(n => n.length > 3);
    if (!clean.length) return;
    try {
      const r = await api.post('/loandisk/director-crosscheck', {
        names: clean,
        exclude_company: companyDetails?.company_name || selectedCompany?.title || "",
      });
      const found = r.data?.matches || [];
      if (found.length) {
        setKnownDirectorMatches(prev => {
          const seen = new Set(prev.map(m => `${m.director}|${m.borrower_id}`));
          return [...prev, ...found.filter(m => !seen.has(`${m.director}|${m.borrower_id}`))];
        });
      }
    } catch (e) {
      console.warn('Director crosscheck failed', e);
    }
  };

  const renderKnownDirectorAlert = () => knownDirectorMatches.length > 0 && (
    <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 mb-4" data-testid="known-director-alert">
      <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-1">
        <AlertTriangle className="w-4 h-4" />
        Known name check — we&apos;ve funded deals with these people before
      </div>
      <ul className="space-y-0.5">
        {knownDirectorMatches.map((m, i) => (
          <li key={i} className="text-xs text-amber-800" data-testid={`known-director-row-${i}`}>
            <span className="font-semibold">{m.director}</span>
            {" — "}{m.borrower_name}{m.business_name ? ` (${m.business_name})` : ""}:{" "}
            {(m.loans || []).map(l =>
              `${l.loan_application_id || `loan ${l.loan_id}`}${l.amount ? ` £${Number(l.amount).toLocaleString()}` : ""}`
            ).join(", ")}
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-amber-700 mt-1">Advisory only — doesn&apos;t block the workflow.</p>
    </div>
  );
  // { name, address, phone, email, dob }
  
  // Generated documents
  const [generatedDocs, setGeneratedDocs] = useState([]);
  
  // DocuSign state
  const [sendingToDocuSign, setSendingToDocuSign] = useState(false);
  const [docuSignResult, setDocuSignResult] = useState(null);
  const [showDocuSignIframe, setShowDocuSignIframe] = useState(false);
  const [docuSignIframeUrl, setDocuSignIframeUrl] = useState(null);
  
  // Signer configuration for DocuSign
  const [signerConfig, setSignerConfig] = useState({});
  // Structure: {
  //   [signerKey]: {
  //     name: string,
  //     email: string,
  //     role: 'signing_director' | 'pg_signer' | 'cross_company_signer',
  //     needsWitness: true,
  //     witnessType: 'signer_selects' | 'pre_assign',
  //     witness: { name, email, address, occupation }
  //   }
  // }
  
  // SimplyFunded internal signer/witness
  const [sfSigner, setSfSigner] = useState({ name: 'Dean Morley', email: 'dean.morley@simplyfunded.co.uk', title: 'Managing Director' });
  const [lenderSignerType, setLenderSignerType] = useState('dean_morley'); // dean_morley | me | other — drives POA line on the docs
  const [sfWitness, setSfWitness] = useState({ name: '', email: '', address: '', occupation: '' });
  const [requireWitness, setRequireWitness] = useState(false); // Only require witness for certain document types (e.g., Debenture)
  // Preloaded company witnesses list (Portal Settings → Company Witnesses)
  const [companyWitnesses, setCompanyWitnesses] = useState([]);
  useEffect(() => {
    api.get('/settings/company-witnesses')
      .then((res) => setCompanyWitnesses(res.data?.witnesses || []))
      .catch(() => {});
  }, []);

  // Load requests and templates on mount
  useEffect(() => {
    loadRequests();
    loadTemplates();
    loadInProgressContracts();
    loadCompletedContracts();
    loadDocuSignEnvelopes();
  }, []);

  // Deep-link from Credit Approved Deals or Docs Out tiles — auto-open the
  // wizard with the deal pre-selected via /legal/onboarding-centre?deal=SF-XXXX
  useEffect(() => {
    if (!deepLinkedDeal) return;
    let cancelled = false;
    (async () => {
      try {
        // status_filter=all so Docs Out / Funded / any deal can be loaded —
        // the default filter ("raise_docs") would hide deals that have
        // already been sent for signing.
        const { data } = await api.get("/contracts/monday/ready-for-docs", {
          params: { search: deepLinkedDeal, limit: 5, status_filter: "all" },
        });
        if (cancelled) return;
        const deal = (data?.deals || []).find(
          (d) => (d.sf_number || "").toLowerCase() === deepLinkedDeal.toLowerCase()
        ) || (data?.deals || [])[0];
        if (deal) {
          // Open the wizard with a synthetic request shape so the rest
          // of the form behaves as if it came from /requests/pending.
          startGeneration({
            application_id: deal.sf_number || deal.monday_item_id,
            company_name: deal.company_name || deal.name,
            loan_amount: deal.loan_amount,
            loan_term_weeks: deal.loan_term_weeks,
            factor_rate: deal.factor_rate,
            payment_frequency: deal.payment_frequency,
            admin_fee: deal.admin_fee,
            legal_fee: 0,
            requested_by: "credit_deals_handoff",
          });
          // Wait for state to settle, then apply the Monday deal pre-fill
          // (offer-pdf, contact, etc.)
          setTimeout(() => handleMondayDealSelect(deal), 200);
          setShowGenerateDialog(true);
          toast.success(`Loaded deal ${deal.sf_number} from Credit Approved Deals`);
        } else {
          toast.info(`No Monday deal matching "${deepLinkedDeal}" — open the wizard and search manually.`);
          if (wizardOnly) setShowGenerateDialog(true);
        }
      } catch (err) {
        console.error("Deep-link load failed:", err);
        toast.error("Could not auto-load deal — please search manually.");
        if (wizardOnly) setShowGenerateDialog(true);
      }
      // Strip the query param so a page refresh doesn't re-trigger
      if (!wizardOnly) setSearchParams({}, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkedDeal]);

  const loadRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await api.get('/contracts/requests/pending');
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error("Error loading requests:", error);
      // Mock data for now
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  const markRequestDone = async (applicationId) => {
    try {
      await api.post(`/contracts/requests/${applicationId}/mark-done`, null, {
        params: { reason: "Handled manually outside system" }
      });
      toast.success("Request marked as done");
      // Refresh the requests list
      loadRequests();
    } catch (error) {
      console.error("Error marking request as done:", error);
      toast.error("Failed to mark request as done");
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await api.get('/contracts/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error loading templates:", error);
      toast.error("Failed to load document templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadInProgressContracts = async () => {
    try {
      const response = await api.get('/contracts/requests/in-progress');
      setInProgressContracts(response.data.contracts || []);
    } catch (error) {
      console.error("Error loading in-progress contracts:", error);
    }
  };

  const loadCompletedContracts = async () => {
    try {
      setLoadingCompleted(true);
      const response = await api.get('/contracts/requests/completed');
      setCompletedContracts({
        contracts_tab: response.data.contracts_tab || [],
        legal_tab: response.data.legal_tab || []
      });
    } catch (error) {
      console.error("Error loading completed contracts:", error);
    } finally {
      setLoadingCompleted(false);
    }
  };

  // Envelope Archive is populated from the LIVE DocuSign envelope list
  // (region=uk) — same source the AUS centre uses — so every UK envelope
  // shows here regardless of whether a DB contract-request row exists.
  const loadDocuSignEnvelopes = async () => {
    try {
      setLoadingEnvelopes(true);
      setEnvelopesError(null);
      const response = await api.get('/contracts/envelopes/list', {
        params: { region: 'uk', days: 90 }
      });
      if (response.data?.success) {
        setAllEnvelopes(response.data.envelopes || []);
      } else {
        setEnvelopesError(response.data?.error || "Could not load envelopes");
      }
    } catch (error) {
      console.error("Error loading DocuSign envelopes:", error);
      setEnvelopesError(error?.response?.data?.detail || "Could not load DocuSign envelopes");
    } finally {
      setLoadingEnvelopes(false);
    }
  };

  // Resume an in-progress envelope
  const resumeEnvelope = async (envelopeId) => {
    try {
      const response = await api.get(`/contracts/envelope/${envelopeId}/resume`);
      
      if (response.data.success && response.data.sender_view_url) {
        setDocuSignIframeUrl(response.data.sender_view_url);
        setDocuSignResult({
          envelope_id: envelopeId,
          ...response.data.envelope
        });
        setShowDocuSignIframe(true);
        setShowGenerateDialog(true);
        toast.success("Resuming DocuSign session...");
      } else {
        toast.info(response.data.message || "Could not resume - envelope may already be sent");
      }
    } catch (error) {
      console.error("Error resuming envelope:", error);
      toast.error("Failed to resume envelope");
    }
  };

  // Mark envelope as complete when user finishes in DocuSign
  const handleDocuSignComplete = async (envelopeId) => {
    try {
      await api.post(`/contracts/envelope/${envelopeId}/mark-complete`);
      
      // Refresh the lists
      await Promise.all([
        loadRequests(),
        loadInProgressContracts(),
        loadCompletedContracts()
      ]);
      
      toast.success("Documents sent for signature successfully!");
      
      // Close the iframe
      setShowDocuSignIframe(false);
      setDocuSignIframeUrl(null);
      
    } catch (error) {
      console.error("Error marking envelope complete:", error);
    }
  };

  // Discard/delete an in-progress envelope
  const discardEnvelope = async (envelopeId, companyName) => {
    if (!confirm(`Are you sure you want to discard the envelope for "${companyName}"?\n\nThis will delete the draft envelope and cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/contracts/envelope/${envelopeId}`);
      toast.success("Envelope discarded");
      
      // Refresh the list
      await loadInProgressContracts();
    } catch (error) {
      console.error("Error discarding envelope:", error);
      toast.error("Failed to discard envelope");
    }
  };

  // Helper to get document type labels from envelope
  const getDocumentTypeLabels = (contract) => {
    const labels = [];
    const docs = contract.documents || [];
    
    // Also check document_types array (from envelope records)
    const docTypes = contract.document_types || [];
    for (const dtype of docTypes) {
      const type = dtype?.toLowerCase() || '';
      if (type === 'ftla' || type.includes('fixed term') || type.includes('loan agreement')) {
        if (!labels.includes('FTLA')) labels.push('FTLA');
      }
      if (type === 'debenture' || type.includes('deb')) {
        if (!labels.includes('Deb')) labels.push('Deb');
      }
      if (type === 'pg' || type.includes('personal guarantee')) {
        if (!labels.includes('PG')) labels.push('PG');
      }
      if (type === 'pg_cross_company' || type.includes('cross_company') || type.includes('x/company')) {
        if (!labels.includes('XCG')) labels.push('XCG');
      }
    }
    
    // Check documents array (from generated documents)
    for (const doc of docs) {
      const type = doc.type?.toLowerCase() || '';
      if (type.includes('ftla') || type.includes('fixed term') || type.includes('loan agreement')) {
        if (!labels.includes('FTLA')) labels.push('FTLA');
      }
      if (type.includes('debenture')) {
        if (!labels.includes('Deb')) labels.push('Deb');
      }
      if (type.includes('personal guarantee') || type.includes('pg')) {
        if (type.includes('cross') || type.includes('x/company')) {
          if (!labels.includes('XCG')) labels.push('XCG');
        } else {
          if (!labels.includes('PG')) labels.push('PG');
        }
      }
    }
    
    return labels.length > 0 ? labels : ['Docs'];
  };

  // Pre-fill the wizard from a Monday.com "Ready for Doc's" deal.
  // Triggered when the operator picks a row in <MondayDealSearch>.
  const fillFeesFromOfferFile = async (deal) => {
    if (!deal?.monday_item_id) return;
    try {
      const { data } = await api.get(`/contracts/monday/offer-terms/${deal.monday_item_id}`);
      const t = data?.terms || {};
      const pulled = {};
      setLoanDetails((prev) => {
        const next = { ...prev };
        if (!prev.adminFee && t.arrangement_fee != null && t.arrangement_fee > 0) {
          next.adminFee = t.arrangement_fee;
          pulled.adminFee = t.arrangement_fee;
        }
        if (!prev.legalFee && t.legal_fee != null && t.legal_fee > 0) {
          next.legalFee = t.legal_fee;
          pulled.legalFee = t.legal_fee;
        }
        return next;
      });
      if (pulled.adminFee != null || pulled.legalFee != null) {
        setOfferSourced((s) => ({
          ...s,
          ...(pulled.adminFee != null ? { adminFee: true } : {}),
          ...(pulled.legalFee != null ? { legalFee: true } : {}),
        }));
        setManualOverrides((m) => ({
          ...m,
          ...(pulled.adminFee != null ? { adminFee: true } : {}),
          ...(pulled.legalFee != null ? { legalFee: true } : {}),
        }));
        const bits = [];
        if (pulled.adminFee != null) bits.push(`Arrangement £${pulled.adminFee.toLocaleString()}`);
        if (pulled.legalFee != null) bits.push(`Legal £${pulled.legalFee.toLocaleString()}`);
        toast.success(`Fees pulled from the offer file: ${bits.join(" · ")}`);
      }
    } catch (e) {
      // No offer file / unreadable — fields stay manual, don't block the wizard
      console.warn("Offer terms lookup failed:", e?.response?.data?.detail || e.message);
    }
  };

  const handleMondayDealSelect = (deal) => {
    if (!deal) return;

    // Loan details — keep any value the operator already typed by only
    // overwriting fields that have a meaningful value on the Monday side.
    setLoanDetails((prev) => ({
      ...prev,
      loanAmount: deal.loan_amount || prev.loanAmount,
      loanTermWeeks: deal.loan_term_weeks || prev.loanTermWeeks,
      factorRate: deal.factor_rate || prev.factorRate,
      paymentFrequency: deal.payment_frequency || prev.paymentFrequency,
      adminFee: deal.admin_fee || prev.adminFee,
      sfNumber: (deal.sf_number || "").replace(/^SF-/i, "") || prev.sfNumber,
    }));

    // Unlock the admin fee field if it was disabled by sales-request lock
    if (deal.admin_fee) {
      setManualOverrides((m) => ({ ...m, adminFee: true }));
    }

    // Fees missing on Monday? Pull them from the offer file itself — the
    // signed Jotform proposal on the Monday item (Paul, 27 Jul 2026).
    fillFeesFromOfferFile(deal);

    // Capture the OfferFormID for the Jotform attachment toggle
    setOfferFormId(deal.offer_form_id || "");
    setAttachOfferForm(!!deal.offer_form_id);

    // Capture the Offer PDF URL pushed by make.com — default to attaching
    setOfferPdfUrl(deal.offer_pdf_url || "");
    setMondayItemId(deal.monday_item_id || null);
    setAttachOfferPdf(!!deal.offer_pdf_url);

    // Auto-detect top-up / consolidation from the deal name (same regex as openGenerateDialog)
    const tuHaystack = [deal.name, deal.company_name].filter(Boolean).join(" ");
    const isTopUp = /\(\s*TU\s*\)|\bTU\b|top[\s-]?up|consolid/i.test(tuHaystack);
    setContractTypes((c) => ({ ...c, useOfFunds: isTopUp }));

    // Kick off Companies House search using the cleaned company name (strip SF prefix + (TU) suffix)
    const cleanedName = (deal.company_name || deal.name || "")
      .replace(/^SF-\d+\s*-?\s*/i, "")
      .replace(/\s*\(\s*TU\s*\)\s*/i, "")
      .trim();
    if (cleanedName) {
      setCompanySearch(cleanedName);
      setTimeout(() => searchCompany(cleanedName), 200);
    }
  };

  // Companies House search
  const searchCompany = async (query, isCrossCompany = false) => {
    if (!query || query.length < 2) return;
    
    try {
      if (isCrossCompany) {
        setSearchingCrossCompany(true);
      } else {
        setSearchingCompany(true);
      }
      
      const response = await api.get(`/companies-house/search?q=${encodeURIComponent(query)}`);
      const results = response.data.items || [];
      
      if (isCrossCompany) {
        setCrossCompanyResults(results);
      } else {
        setCompanySearchResults(results);
      }
    } catch (error) {
      console.error("Company search error:", error);
      toast.error(errorDetailMessage(error, "Failed to search Companies House"));
    } finally {
      if (isCrossCompany) {
        setSearchingCrossCompany(false);
      } else {
        setSearchingCompany(false);
      }
    }
  };

  // Get company details including directors
  const getCompanyDetails = async (companyNumber, isCrossCompany = false) => {
    try {
      if (isCrossCompany) {
        setLoadingCompanyDetails(true);
      } else {
        setLoadingCompanyDetails(true);
      }
      
      // Get company profile
      const profileResponse = await api.get(`/companies-house/company/${companyNumber}`);
      const profile = profileResponse.data;
      
      // Get officers (directors)
      const officersResponse = await api.get(`/companies-house/company/${companyNumber}/officers`);
      const officers = officersResponse.data.items || [];
      
      // Filter to active directors only
      const activeDirectors = officers.filter(o => 
        o.officer_role === "director" && !o.resigned_on
      );
      
      // Advisory: flag directors we've already funded deals with (LoanDisk)
      runDirectorCrosscheck(activeDirectors.map(d => d.name));
      
      const details = {
        ...profile,
        directors: activeDirectors
      };
      
      if (isCrossCompany) {
        setCrossCompanyDetails(details);
        setSelectedCrossCompany({
          company_number: companyNumber,
          title: profile.company_name
        });
      } else {
        setCompanyDetails(details);
        setDirectors(activeDirectors);
        setSelectedCompany({
          company_number: companyNumber,
          title: profile.company_name
        });
      }
      
    } catch (error) {
      console.error("Error getting company details:", error);
      toast.error(errorDetailMessage(error, "Failed to load company details"));
    } finally {
      setLoadingCompanyDetails(false);
    }
  };

  // UK Postcode/Address search — proxied through the backend (API key server-side)
  const searchAddress = async (postcode) => {
    if (!postcode || postcode.length < 3) return;

    try {
      setSearchingAddress(true);
      const data = await postcodeAddresses(postcode);
      if (data.ok && (data.addresses || []).length > 0) {
        setAddressSearchResults(data.addresses);
      } else {
        setAddressSearchResults([]);
        toast.error(data.error || "No addresses found for this postcode");
      }
    } catch (error) {
      console.error("Address search error:", error);
      toast.error("Failed to search addresses");
    } finally {
      setSearchingAddress(false);
    }
  };

  // Toggle manual override for a field (with confirmation)
  const toggleManualOverride = (field) => {
    if (!manualOverrides[field]) {
      // Turning on override - show confirmation
      if (window.confirm(`Are you sure you want to manually override the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}? This data comes from Monday.com and should normally not be changed.`)) {
        setManualOverrides(prev => ({ ...prev, [field]: true }));
        toast.info(`Manual override enabled for ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
      }
    } else {
      // Turning off override
      setManualOverrides(prev => ({ ...prev, [field]: false }));
    }
  };

  // Load existing bank details for an application (check if CoP was done in Stage 2b)
  const loadBankDetails = async (applicationId) => {
    try {
      setLoadingBankDetails(true);
      setCopStatus(null);
      setCopResult(null);
      
      const response = await api.get(`/contracts/bank-details/${applicationId}`);
      
      if (response.data.found && response.data.bank_details) {
        const bd = response.data.bank_details;
        setBankDetails({
          accountNumber: bd.account_number || "",
          sortCode: bd.sort_code || "",
          accountName: bd.account_name || "",
          bankName: bd.bank_name || "",
          verified: bd.verified || false,
          verifiedBy: bd.verified_by || null,
          verifiedAt: bd.verified_at || null
        });
        setBankDetailsSource(response.data.source); // "stage_2b" or "pending"
        
        // Defensive: if API claims cop_verified but actual bank values are blank,
        // treat as NOT verified so the user isn't locked into an empty form (SF-23462 Leanne Drane bug).
        const hasUsableBankValues = !!(bd.sort_code && bd.account_number);
        
        if (response.data.cop_verified && hasUsableBankValues) {
          setCopStatus("verified");
          toast.success("Bank details already CoP verified from Stage 2b");
        } else if (response.data.cop_verified && !hasUsableBankValues) {
          // Stale verified flag but no actual values — let user enter them manually.
          setCopStatus(null);
          toast.warning("Bank details were marked verified but values are missing — please re-enter.");
        } else if (response.data.requires_cop) {
          // Bank details exist but not yet CoP verified
          toast.info("Bank details require CoP verification");
        }
      } else {
        // No bank details found - will need to enter and verify
        setBankDetails({
          accountNumber: "",
          sortCode: "",
          accountName: companyDetails?.company_name || selectedRequest?.company_name || "",
          bankName: "",
          verified: false,
          verifiedBy: null,
          verifiedAt: null
        });
        setBankDetailsSource(null);
      }
    } catch (error) {
      console.error("Error loading bank details:", error);
      // Pre-fill account name with company name
      setBankDetails(prev => ({
        ...prev,
        accountName: companyDetails?.company_name || selectedRequest?.company_name || ""
      }));
    } finally {
      setLoadingBankDetails(false);
    }
  };

  // Start new document generation (from request or fresh)
  const startGeneration = (request = null) => {
    setSelectedRequest(request);
    setCurrentStep(1);
    
    // Determine if this is from sales (has application data)
    const fromSales = !!(request?.application_id && !request.application_id.startsWith('MANUAL'));
    setIsFromSalesRequest(fromSales);
    
    // Reset manual overrides
    setManualOverrides({
      loanAmount: false,
      loanTermWeeks: false,
      factorRate: false,
      paymentFrequency: false,
      adminFee: false,
      legalFee: false,
      company: false,
      bankDetails: false
    });
    
    // Reset form
    // Auto-detect top-up / consolidation deals by looking for "(TU)" in the
    // application name, business name, or notes. Operator can still untick if wrong.
    const tuHaystack = [
      request?.name,
      request?.business_name,
      request?.company_name,
      request?.notes,
      request?.facility_type,
    ].filter(Boolean).join(" ");
    const isTopUp = /\(\s*TU\s*\)|\bTU\b|top[\s-]?up|consolid/i.test(tuHaystack);
    setContractTypes({
      ftla: true,
      debenture: false,
      guaranteeType: "pg",
      useOfFunds: isTopUp,
    });

    // Pre-fill Use of Funds breakdown from the credit team's net-in-hand data
    // (existing SF#, outstanding balance, consolidation lenders) so operators
    // don't have to re-key data that's already on the application record.
    const prefilledLenders = (request?.consolidation_lenders || [])
      .filter((l) => l && (l.name || l.amount))
      .map((l) => ({
        name: l.name || "",
        amount: parseFloat(l.amount) || 0,
      }))
      .slice(0, 10);
    setUseOfFundsBreakdown({
      mode:
        prefilledLenders.length > 0 && (request?.existing_loan_sf || parseFloat(request?.existing_loan_outstanding))
          ? "both"
          : prefilledLenders.length > 0
          ? "consolidation"
          : "topup",
      existingSfNumber: request?.existing_loan_sf || "",
      existingSfBalance:
        parseFloat(request?.existing_loan_outstanding) ||
        parseFloat(request?.existing_loan_balance) ||
        0,
      lenders: prefilledLenders.length > 0 ? prefilledLenders : [{ name: "", amount: 0 }],
      selectedLoandiskLoanId: null,
    });
    // Reset offer-form state — picked up when operator selects a Monday deal
    setOfferFormId("");
    setAttachOfferForm(false);
    setOfferPdfUrl("");
    setAttachOfferPdf(true);
    setMondayItemId(null);
    setOfferSourced({});
    const hasExistingSf = !!request?.existing_loan_sf;
    const hasExistingBalance =
      !!(parseFloat(request?.existing_loan_outstanding) || parseFloat(request?.existing_loan_balance));
    if (hasExistingSf || hasExistingBalance || prefilledLenders.length > 0) {
      setUseOfFundsPrefill({
        source: "application",
        hasExistingSf,
        hasExistingBalance,
        lenderCount: prefilledLenders.length,
      });
    } else {
      setUseOfFundsPrefill(null);
    }
    setLoanDetails({
      loanAmount: request?.loan_amount || 0,
      loanTermWeeks: request?.loan_term_weeks || 26,
      factorRate: request?.factor_rate || 1.5, // Default factor rate
      paymentFrequency: request?.payment_frequency || "daily",
      paymentDay: "Tuesday",
      firstPaymentDate: "",
      adminFee: request?.admin_fee || 0, // Admin/arrangement fee from Monday.com
      legalFee: request?.legal_fee || 0 // Legal fee
    });
    setSelectedCompany(null);
    setCompanyDetails(null);
    setCompanySearch("");
    setCompanySearchResults([]);
    setDirectors([]);
    setSigningDirectors([]);
    setPgDirectors([]);
    setDirectorAddresses({});
    setDirectorPhones({});
    setDirectorEmails({});
    setDirectorDobDays({});
    setSelectedCrossCompany(null);
    setCrossCompanyDetails(null);
    setCrossCompanySearch("");
    setCrossCompanySigningDirector(null);
    setCrossCompanies([]); // Reset multiple cross companies
    setManualPgGuarantors([]); // Reset manual PG guarantors
    setGeneratedDocs([]);
    setAddressSearchResults([]);
    setActiveAddressDirector(null);
    
    // Reset DocuSign state
    setDocuSignResult(null);
    setSignerConfig({});
    setSfSigner({ name: 'Dean Morley', email: 'dean.morley@simplyfunded.co.uk', title: 'Managing Director' });
    setLenderSignerType('dean_morley');
    setSfWitness({ name: '', email: '', address: '', occupation: '' });
    setRequireWitness(false);
    
    // Reset bank details
    setBankDetails({
      accountNumber: "",
      sortCode: "",
      accountName: "",
      bankName: "",
      verified: false,
      verifiedBy: null,
      verifiedAt: null
    });
    setBankDetailsSource(null);
    
    // Pre-fill from request if available - and AUTO-SEARCH if from sales
    if (request) {
      setCompanySearch(request.company_name || "");
      if (request.company_number) {
        // Auto-load company details immediately for sales requests
        getCompanyDetails(request.company_number);
      } else if (request.company_name && fromSales) {
        // If no company number but have name, trigger a search
        setTimeout(() => {
          searchCompany(request.company_name);
        }, 500);
      }
      
      // Load any existing bank details for this application
      if (request.application_id) {
        loadBankDetails(request.application_id);
      }
    }
    
    setShowGenerateDialog(true);
  };

  // Generate documents
  const generateDocuments = async () => {
    try {
      setGenerating(true);
      
      // Helper: build a full DD/MM/YYYY DOB from Companies House (which redacts day)
      // + the operator-entered day. Falls back to "01" if not yet entered.
      const buildDob = (director) => {
        if (!director?.date_of_birth) return "";
        const day = directorDobDays[director.name] || director.date_of_birth.day || "01";
        const month = String(director.date_of_birth.month || "01").padStart(2, "0");
        const dayPadded = String(day).padStart(2, "0");
        return `${dayPadded}/${month}/${director.date_of_birth.year || "1970"}`;
      };
      
      // Prepare PG directors with full details (addresses, emails, DOB, phone)
      const linksFor = (name) => contractTypes.guaranteeType === "pg_cross_company" ? (pgCompanyLinks[name] || []) : null;
      const pgDirectorData = pgDirectors.map(directorName => {
        const director = directors.find(d => d.name === directorName) || {};
        return {
          name: directorName,
          residential_address: directorAddresses[directorName] || "",
          date_of_birth: buildDob(director),
          email: directorEmails[directorName] || director.email || "",
          telephone: directorPhones[directorName] || director.telephone || "",
          cross_company_numbers: linksFor(directorName),
        };
      });
      
      // Add manual PG guarantors (third party PGs)
      const manualPgData = manualPgGuarantors.map(pg => ({
        name: pg.name,
        residential_address: pg.address,
        date_of_birth: pg.dob || "",
        email: pg.email,
        telephone: pg.phone,
        cross_company_numbers: linksFor(pg.name),
      }));
      
      // Combine director PGs and manual PGs (max 6 total — each gets their own deed; FTLA names the first 3)
      const allPgDirectors = [...pgDirectorData, ...manualPgData].slice(0, 6);
      
      // Build signing directors (multi-select) — up to 6 (UoF/Debenture print a block each; FTLA names the first 3).
      const signingDirectorsData = signingDirectors.slice(0, 6).map(directorName => {
        const director = directors.find(d => d.name === directorName) || {};
        return {
          name: directorName,
          needs_personal_guarantee: pgDirectors.includes(directorName),
          email: directorEmails[directorName] || director.email || "",
          telephone: directorPhones[directorName] || director.telephone || "",
          date_of_birth: buildDob(director),
          residential_address: directorAddresses[directorName] || ""
        };
      });
      const primarySigningDirector = signingDirectors[0] || null;
      
      // Extract postcode from registered address
      const registeredAddress = companyDetails?.registered_office_address;
      const fullAddress = registeredAddress
        ? `${registeredAddress.address_line_1 || ''}, ${registeredAddress.locality || ''}, ${registeredAddress.postal_code || ''}`
        : "";
      
      // Determine application ID - use sales request ID, or manual SF number, or generate one
      const applicationId = selectedRequest?.application_id 
        || (loanDetails.sfNumber ? `SF-${String(loanDetails.sfNumber).replace(/^SF-?/i, '')}` : `MANUAL-${Date.now()}`);
      
      const requestData = {
        application_id: applicationId,
        generate_ftla: contractTypes.ftla,
        generate_debenture: contractTypes.debenture,
        guarantee_type: contractTypes.guaranteeType,
        company_number: selectedCompany?.company_number || companyDetails?.company_number,
        company_name: companyDetails?.company_name || selectedCompany?.title,
        registered_address: fullAddress,
        registered_postcode: registeredAddress?.postal_code || "",
        incorporation_date: companyDetails?.date_of_creation || "",
        telephone: selectedRequest?.telephone || loanDetails.customerPhone || "",
        signing_director: primarySigningDirector
          ? {
              name: primarySigningDirector,
              needs_personal_guarantee: pgDirectors.includes(primarySigningDirector)
            }
          : { name: "", needs_personal_guarantee: false },
        signing_directors: signingDirectorsData, // NEW: full multi-signer payload (up to 3)
        pg_directors: allPgDirectors, // Combined director PGs + manual third-party PGs
        // Support multiple cross companies (up to 6)
        cross_company: crossCompanies.length > 0 ? crossCompanies[0] : null,
        cross_companies: crossCompanies.slice(1), // Additional cross companies (2-6)
        loan_amount: loanDetails.loanAmount || selectedRequest?.loan_amount || 0,
        loan_term_weeks: loanDetails.loanTermWeeks || 26,
        factor_rate: loanDetails.factorRate || 1.5,
        admin_fee: loanDetails.adminFee || 0,
        legal_fee: loanDetails.legalFee || 0,
        payment_frequency: loanDetails.paymentFrequency || "daily",
        payment_day: loanDetails.paymentFrequency === "weekly" ? (loanDetails.paymentDay || "Tuesday") : null,
        first_payment_date: loanDetails.firstPaymentDate || null,
        lender_signer_name: sfSigner.name || "Dean Morley",
        lender_signer_title: sfSigner.title || "Managing Director",
        lender_signer_authority: lenderSignerType === "dean_morley"
          ? ""
          : "Authorised Signatory under Power Of Attorney",
        // Include bank details for FTLA
        bank_details: bankDetails.accountNumber ? {
          account_number: bankDetails.accountNumber,
          sort_code: bankDetails.sortCode,
          account_name: bankDetails.accountName,
          bank_name: bankDetails.bankName
        } : null,
        // Use of Funds & Disbursement Statement (top-up / consolidation flag)
        generate_use_of_funds: contractTypes.useOfFunds,
        // Mode-aware payload: only send the fields that match the chosen deal type
        existing_sf_agreement_number:
          contractTypes.useOfFunds &&
          ["topup", "both"].includes(useOfFundsBreakdown.mode || "topup")
            ? (useOfFundsBreakdown.existingSfNumber || null)
            : null,
        existing_sf_balance:
          contractTypes.useOfFunds &&
          ["topup", "both"].includes(useOfFundsBreakdown.mode || "topup")
            ? (parseFloat(useOfFundsBreakdown.existingSfBalance) || 0)
            : 0,
        third_party_lenders:
          contractTypes.useOfFunds &&
          ["consolidation", "both"].includes(useOfFundsBreakdown.mode || "topup")
            ? (useOfFundsBreakdown.lenders || [])
                .filter((l) => (l.name || "").trim() && (parseFloat(l.amount) || 0) > 0)
                .slice(0, 10)
                .map((l) => ({
                  name: l.name.trim(),
                  amount: parseFloat(l.amount) || 0,
                  reference: (l.reference || "").trim(),
                }))
            : [],
      };
      
      const response = await api.post('/contracts/generate', requestData);
      
      if (response.data.success) {
        setGeneratedDocs(response.data.documents || []);
        toast.success(`Generated ${response.data.documents?.length || 0} document(s)`);
        if (response.data.bank_details_notice) {
          // The bank details did not reach Credit's queue: say so while the person can still act on it.
          toast.warning(response.data.bank_details_notice, { duration: 15000 });
        }
        setCurrentStep(6); // Go to Results/DocuSign step
      } else {
        toast.error("Document generation failed: " + (response.data.errors?.join(", ") || "Unknown error"));
      }
      
    } catch (error) {
      console.error("Document generation error:", error);
      toast.error(errorDetailMessage(error, "Failed to generate documents"));
    } finally {
      setGenerating(false);
    }
  };

  // Send documents to DocuSign for e-signature
  // Sales ask (Sep 2026): Word download + replace-before-DocuSign
  const [wordBusy, setWordBusy] = useState(null);
  const downloadDocAsWord = async (doc) => {
    const idx = generatedDocs.indexOf(doc);
    setWordBusy(idx);
    try {
      const blob = await contractsDocAsWord(doc.result.file_url, `${doc.type} - ${companyDetails?.company_name || "document"}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${doc.type} - ${companyDetails?.company_name || "document"}.docx`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Word version downloaded — edit it, then use Replace to send your version to DocuSign");
    } catch (e) {
      let msg = e?.message;
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* ignore */ }
      toast.error("Word conversion failed: " + msg);
    } finally { setWordBusy(null); }
  };
  const handleReplaceDoc = (idx, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File too large (15 MB max)"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(",")[1];
      const isDocx = f.name.toLowerCase().endsWith(".docx");
      setGeneratedDocs(prev => prev.map((d, i) => i === idx ? {
        ...d, replaced: true, file_base64: b64, filename: f.name,
        content_type: isDocx ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf",
      } : d));
      toast.success(`${f.name} will replace the generated ${generatedDocs[idx]?.type} in the DocuSign envelope`);
    };
    reader.readAsDataURL(f);
  };

  const sendToDocuSign = async () => {
    if (generatedDocs.length === 0) {
      toast.error("No documents to send");
      return;
    }
    
    // Build signers array from signerConfig
    const signers = [];
    
    // Add ALL company signing directors (multi-signer support for FTLA)
    for (const sd of signingDirectors) {
      const config = signerConfig[`company_${sd}`] || {};
      const email = config.email || directorEmails[sd] || directors.find(d => d.name === sd)?.email;
      
      if (!email) {
        toast.error(`Please enter email address for Company Signing Director: ${sd}`);
        return;
      }
      
      signers.push({
        name: sd,
        email,
        role: "signing_director",
        needsWitness: config.needsWitness !== false,
        witnessType: config.witnessType || 'signer_selects',
        witness: config.witness || null
      });
    }
    
    // Add PG signers (directors + manual third-party guarantors)
    const allPgSignerNames = [...pgDirectors, ...manualPgGuarantors.map(pg => pg.name)];
    for (const pgDir of allPgSignerNames) {
      const config = signerConfig[`pg_${pgDir}`] || {};
      const email = config.email || directorEmails[pgDir]
        || directors.find(d => d.name === pgDir)?.email
        || manualPgGuarantors.find(p => p.name === pgDir)?.email;
      
      if (!email) {
        toast.error(`Please enter email address for Personal Guarantor: ${pgDir}`);
        return;
      }
      
      signers.push({
        name: pgDir,
        email,
        role: "pg_signer",
        needsWitness: config.needsWitness !== false, // Deed default — PGs must be witnessed
        witnessType: config.witnessType || 'signer_selects',
        witness: config.witness || null
      });
    }
    
    // Add Cross Company Guarantee signers — every XCG company's authorised
    // signatory signs their own deed block on the PG With X/Company doc.
    // Duplicates (e.g. XCG signatory who is also a director/PG) are merged
    // into one signing session by the backend.
    if (contractTypes.guaranteeType === "pg_cross_company") {
      for (const cc of crossCompanies) {
        const key = `xcg_${cc.company_number}`;
        const config = signerConfig[key] || {};
        const signerName = config.name || cc.signing_director_name;
        const email = config.email
          || directorEmails[cc.signing_director_name]
          || directors.find(d => d.name === cc.signing_director_name)?.email;
        
        if (!email) {
          toast.error(`Please enter email address for Cross Company signatory: ${signerName} (${cc.company_name})`);
          return;
        }
        
        signers.push({
          name: signerName,
          email,
          role: "cross_company_signer",
          company: cc.company_name,
          needsWitness: config.needsWitness !== false, // XCG blocks are deeds — witnessed by default
          witnessType: config.witnessType || 'signer_selects',
          witness: config.witness || null
        });
      }
    }
    
    // Validate SF signer
    if (!sfSigner.name || !sfSigner.email) {
      toast.error("Please enter SimplyFunded Authorised Signatory details");
      return;
    }
    
    // Validate SF witness only if witness is required (e.g., for Debenture)
    if (requireWitness && (!sfWitness.name || !sfWitness.email)) {
      toast.error("Please enter SimplyFunded Witness details");
      return;
    }
    
    // Add SF signer
    signers.push({
      name: sfSigner.name,
      email: sfSigner.email,
      role: "lender_signer",
      title: sfSigner.title
    });
    
    // Add SF witness only if required
    if (requireWitness && sfWitness.name && sfWitness.email) {
      signers.push({
        name: sfWitness.name,
        email: sfWitness.email,
        role: "lender_witness",
        address: sfWitness.address,
        occupation: sfWitness.occupation
      });
    }
    
    if (signers.length === 0) {
      toast.error("At least one signer is required");
      return;
    }
    
    try {
      setSendingToDocuSign(true);
      
      // Prepare documents for DocuSign (order = envelope order; uploads carry base64)
      const documents = generatedDocs.map(doc => ({
        type: doc.type,
        file_url: doc.result?.file_url,
        template_id: doc.template_id,
        guarantor: doc.guarantor || undefined,
        cross_companies: doc.cross_companies || undefined,
        file_base64: doc.file_base64 || undefined,
        filename: doc.filename || undefined,
        content_type: doc.content_type || undefined
      }));
      
      // Determine application ID
      const applicationId = selectedRequest?.application_id 
        || (loanDetails.sfNumber ? `SF-${String(loanDetails.sfNumber).replace(/^SF-?/i, '')}` : `MANUAL-${Date.now()}`);
      
      // Return URL after tagging is complete - this will be handled by the iframe
      const returnUrl = `${window.location.origin}/legal/documents?docusign_complete=true&envelope_id=ENVELOPE_ID`;
      
      const response = await api.post('/contracts/send-to-docusign', {
        application_id: applicationId,
        company_name: companyDetails?.company_name || selectedCompany?.title || "Unknown Company",
        documents,
        signers,
        email_subject: `Loan Agreement - ${companyDetails?.company_name || "Customer"} - ${applicationId}`.trim(),
        email_message: `Please review and sign the attached loan agreement documents. Reference: ${applicationId}`,
        sent_by: "legal_team",
        return_url: returnUrl,
        region: "uk",  // UK documents use UK brand/email
        // Attach the offer PDF proposal as the last document so the customer
        // always sees the offer they were proposed.
        offer_pdf_url: offerPdfUrl || null,
        attach_offer_pdf: (!!offerPdfUrl || !!mondayItemId) && attachOfferPdf,
        monday_item_id: mondayItemId,
      });
      
      if (response.data.success) {
        setDocuSignResult(response.data);
        
        // If we have a sender view URL, show it in iframe
        if (response.data.sender_view_url) {
          toast.success("Opening DocuSign tagging interface...");
          setDocuSignIframeUrl(response.data.sender_view_url);
          setShowDocuSignIframe(true);
        } else if (response.data.is_mock) {
          toast.info("DocuSign is in mock mode - In production, this would open DocuSign's tagging interface");
        } else {
          toast.success(`Draft envelope created! Envelope ID: ${response.data.envelope_id}`);
        }
        if (response.data.offer_pdf_attached) {
          toast.success("Offer PDF proposal attached as the last document in the envelope.");
        } else if (offerPdfUrl && attachOfferPdf && response.data.offer_pdf_error) {
          toast.warning(`Offer PDF could not be attached: ${response.data.offer_pdf_error}`);
        }
        const at = response.data.auto_tabs;
        if (at?.placed) {
          const who = [...new Set((at.recipients || []).map(r => r.recipient))];
          const pinned = at.resolution?.pinned ? ` — ${at.resolution.pinned} fields pinned to their pages (each guarantor only on their own deed)` : "";
          toast.success(`Signature fields pre-placed for ${who.length} recipient${who.length === 1 ? "" : "s"} (${who.join(", ")})${pinned} — check them in DocuSign before sending.`, { duration: 9000 });
        }
        if (at?.warnings?.length) {
          toast.warning(at.warnings.join(" · "), { duration: 14000 });
        }
      } else {
        toast.error("Failed to create DocuSign envelope");
      }
      
    } catch (error) {
      console.error("DocuSign error:", error);
      toast.error(error.response?.data?.detail || "Failed to create DocuSign envelope");
    } finally {
      setSendingToDocuSign(false);
    }
  };

  // Toggle PG director
  const togglePgDirector = (directorName) => {
    setPgDirectors(prev => {
      if (prev.includes(directorName)) {
        // Remove director
        const newAddresses = { ...directorAddresses };
        delete newAddresses[directorName];
        setDirectorAddresses(newAddresses);
        return prev.filter(d => d !== directorName);
      } else {
        // Add director
        return [...prev, directorName];
      }
    });
  };

  // ---- Envelope doc list helpers (re-order / preview / extra uploads) ----
  const moveGeneratedDoc = (idx, dir) => {
    setGeneratedDocs(prev => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const previewDoc = async (doc) => {
    if (doc.file_base64) {
      const bytes = atob(doc.file_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      // The generated document's declared type is not trusted: only an allowlisted type is framed, and
      // anything else is saved instead.
      const raw = new Blob([arr], { type: doc.content_type || "application/pdf" });
      const { blob, kind } = classifyBlob(raw);
      if (kind === "download") {
        openBlobInNewTab(raw, doc.type);
        toast.info("No inline preview for this file type — downloaded instead");
        return;
      }
      const url = URL.createObjectURL(blob);
      setDocPreview({ url, name: doc.type, blob: true });
    } else if (doc.result?.file_url) {
      // Docupilot S3 links force a download — proxy through the backend so
      // the PDF opens inline in the viewer instead.
      setDocPreview({ url: null, name: doc.type, loading: true });
      try {
        const blob = await contractsDocInline(doc.result.file_url);
        const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        setDocPreview({ url, name: doc.type, blob: true });
      } catch (e) {
        setDocPreview(null);
        toast.error([424, 502].includes(e.response?.status)
          ? "This document link has expired — regenerate the document to view it"
          : "Preview failed — try downloading instead");
      }
    }
  };

  const handleExtraDocUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = String(reader.result).split(",")[1];
        setGeneratedDocs(prev => [...prev, {
          type: file.name.replace(/\.pdf$/i, ""),
          custom_upload: true,
          file_base64: base64,
          filename: file.name,
          content_type: file.type || "application/pdf",
        }]);
        toast.success(`${file.name} added to the envelope list`);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  // "View Offer" — always fetch a fresh URL (stored S3 links expire), then
  // proxy through the backend so the PDF previews inline instead of downloading.
  const openOfferView = async () => {
    let url = offerPdfUrl || null;
    if (mondayItemId) {
      try {
        const { data } = await api.get(`/contracts/monday/offer-file/${mondayItemId}`);
        if (data.url) url = data.url;
      } catch { /* fall back to the stored URL */ }
    }
    if (!url) {
      toast.info("No offer PDF found for this deal");
      return;
    }
    setOfferViewUrl({ url: null, loading: true });
    try {
      const blob = await contractsDocInline(url);
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      setOfferViewUrl({ url: blobUrl, blob: true });
    } catch (e) {
      setOfferViewUrl(null);
      toast.error([424, 502].includes(e.response?.status)
        ? "The offer link has expired — re-upload the offer PDF on the Monday item"
        : "Offer preview failed");
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1ContractSelection();
      case 2:
        return renderStep2CompanySelection();
      case 3:
        return renderStep3DirectorSelection();
      case 4:
        return renderStep4BankDetails();
      case 5:
        return renderStep5CrossCompany();
      case 6:
        return renderStep6Results();
      default:
        return null;
    }
  };

  // Step 1: Contract Selection
  const renderStep1ContractSelection = () => (
    <div className="space-y-6">
      {/* Monday.com Deal Search — pre-populate loan details from a "Ready for Doc's" deal */}
      <MondayDealSearch onSelect={handleMondayDealSelect} />

      {/* Sales Request Banner - shows when editing a request from sales */}
      {isFromSalesRequest && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Sales Request - Loan details are locked to maintain data integrity</span>
          </div>
        </div>
      )}
      
      <div>
        <h3 className="text-lg font-semibold mb-4">Select Contracts to Generate</h3>
        
        <div className="space-y-4">
          {/* FTLA - Always required */}
          <div className="flex items-center space-x-3 p-3 border rounded-lg bg-blue-50 border-blue-200">
            <Checkbox id="ftla" checked={true} disabled />
            <div className="flex-1">
              <Label htmlFor="ftla" className="font-medium">Fixed Term Loan Agreement (FTLA)</Label>
              <p className="text-sm text-muted-foreground">Required for all deals</p>
            </div>
            <Badge>Required</Badge>
          </div>
          
          {/* Debenture - Optional */}
          <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
            <Checkbox 
              id="debenture" 
              checked={contractTypes.debenture}
              onCheckedChange={(checked) => {
                setContractTypes(prev => ({ ...prev, debenture: checked }));
                // Auto-enable witness requirement when Debenture is selected
                if (checked) {
                  setRequireWitness(true);
                }
              }}
            />
            <div className="flex-1">
              <Label htmlFor="debenture" className="font-medium cursor-pointer">Debenture</Label>
              <p className="text-sm text-muted-foreground">Optional security document</p>
            </div>
            <Badge variant="outline">Optional</Badge>
          </div>
          
          {/* Personal Guarantee Type */}
          <div className="p-4 border rounded-lg">
            <Label className="font-medium mb-3 block">Personal Guarantee Type</Label>
            <RadioGroup 
              value={contractTypes.guaranteeType} 
              onValueChange={(value) => setContractTypes(prev => ({ ...prev, guaranteeType: value }))}
              className="space-y-3"
            >
              <div className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50">
                <RadioGroupItem value="pg" id="pg" />
                <Label htmlFor="pg" className="flex-1 cursor-pointer">
                  <span className="font-medium">Personal Guarantee</span>
                  <p className="text-sm text-muted-foreground">Standard personal guarantee</p>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50">
                <RadioGroupItem value="pg_cross_company" id="pg_cross" />
                <Label htmlFor="pg_cross" className="flex-1 cursor-pointer">
                  <span className="font-medium">Personal Guarantee with Cross Company Guarantee</span>
                  <p className="text-sm text-muted-foreground">PG plus additional company guarantee</p>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Use of Funds / Disbursement toggle */}
          <div className="pt-2 border-t">
            <button
              type="button"
              onClick={() => setContractTypes(prev => ({ ...prev, useOfFunds: !prev.useOfFunds }))}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                contractTypes.useOfFunds
                  ? "bg-amber-50 border-amber-300 ring-1 ring-amber-300"
                  : "bg-white border-slate-200 hover:bg-slate-50"
              }`}
              data-testid="use-of-funds-toggle"
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={contractTypes.useOfFunds}
                  onChange={() => {}}
                  className="mt-1 pointer-events-none"
                  readOnly
                />
                <div className="flex-1">
                  <p className="font-medium text-sm">
                    Select here if deal is a top up or consolidation loan involving us paying off other lenders directly
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Adds a <span className="font-semibold">Use of Funds & Disbursement Statement</span> to the DocuSign envelope. Fill in the breakdown below (or review it again at the final step).
                  </p>
                </div>
              </div>
            </button>

            {/* Inline UoF table — fill in here so it isn't missed before Step 5 */}
            {contractTypes.useOfFunds && (
              <div className="mt-3">
                <UseOfFundsForm
                  loanAmount={loanDetails.loanAmount}
                  arrangementFee={loanDetails.adminFee}
                  breakdown={useOfFundsBreakdown}
                  onChange={setUseOfFundsBreakdown}
                  prefilledSummary={useOfFundsPrefill}
                  companyName={companyDetails?.company_name || selectedCompany?.title || companySearch || ""}
                />
              </div>
            )}
          </div>

          {/* Offer PDF proposal — attach to DocuSign envelope (auto-pulled from Monday) */}
          {(offerPdfUrl || offerFormId) && (
            <div className="pt-2 border-t" data-testid="offer-form-attach-block">
              {offerPdfUrl ? (
                <label className="flex items-start gap-3 p-3 rounded-lg border border-emerald-300 bg-emerald-50 cursor-pointer hover:bg-emerald-100/60 transition-colors">
                  <input
                    type="checkbox"
                    checked={attachOfferPdf}
                    onChange={(e) => setAttachOfferPdf(e.target.checked)}
                    className="mt-1"
                    data-testid="attach-offer-pdf-checkbox"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      Attach Offer Proposal PDF to envelope
                    </p>
                    <p className="text-xs text-emerald-800 mt-1">
                      The customer's signed Jotform offer proposal will be appended as the last document in the DocuSign envelope, so they always see the offer they were proposed.
                    </p>
                    <a
                      href={offerPdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-blue-700 hover:underline font-mono break-all"
                    >
                      {offerPdfUrl}
                    </a>
                  </div>
                </label>
              ) : (
                <label className="flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
                  <input
                    type="checkbox"
                    checked={false}
                    disabled
                    className="mt-1 opacity-50"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">Attach Offer Proposal PDF to envelope</p>
                      <Badge variant="outline" className="text-[10px] bg-slate-100 border-slate-300 text-slate-600">
                        No PDF on Monday yet
                      </Badge>
                    </div>
                    <p className="text-xs text-amber-800 mt-1">
                      Jotform ID <span className="font-mono">{offerFormId}</span> captured, but make.com hasn't pushed the signed proposal URL to Monday's <span className="font-mono">OfferPDFID</span> column for this deal yet. Older deals won't have it.
                    </p>
                  </div>
                </label>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Application Reference - SF Number */}
      {!isFromSalesRequest && (
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-4">Application Reference</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sfNumber">SF Number</Label>
              <div className="flex gap-2">
                <span className="flex items-center px-3 bg-gray-100 border border-r-0 rounded-l-md text-sm text-gray-600">SF-</span>
                <Input
                  id="sfNumber"
                  type="text"
                  value={loanDetails.sfNumber || ""}
                  onChange={(e) => setLoanDetails(prev => ({ ...prev, sfNumber: e.target.value.replace(/[^0-9]/g, '') }))}
                  placeholder="e.g., 15740"
                  className="rounded-l-none"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Enter the SimplyFunded application reference number</p>
            </div>
            <div>
              <Label htmlFor="customerPhone">Customer Telephone</Label>
              <Input
                id="customerPhone"
                type="tel"
                value={loanDetails.customerPhone || ""}
                onChange={(e) => setLoanDetails(prev => ({ ...prev, customerPhone: e.target.value }))}
                placeholder="e.g., 07700 900123"
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Show SF Number for sales requests (read-only) */}
      {isFromSalesRequest && selectedRequest?.application_id && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Application Reference: {selectedRequest.application_id}</span>
          </div>
        </div>
      )}
      
      {/* Loan Details Section */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold mb-4">Loan Details</h3>
        <div className="grid grid-cols-2 gap-4">
          {/* Loan Amount */}
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="loanAmount">Loan Amount (£)</Label>
              {isFromSalesRequest && !manualOverrides.loanAmount && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700"
                  onClick={() => toggleManualOverride('loanAmount')}
                >
                  Manually Enter
                </Button>
              )}
            </div>
            <Input
              id="loanAmount"
              type="number"
              value={loanDetails.loanAmount}
              onChange={(e) => setLoanDetails(prev => ({ ...prev, loanAmount: parseFloat(e.target.value) || 0 }))}
              placeholder="e.g., 40000"
              disabled={isFromSalesRequest && !manualOverrides.loanAmount}
              className={isFromSalesRequest && !manualOverrides.loanAmount ? "bg-gray-100 cursor-not-allowed" : ""}
            />
            {isFromSalesRequest && !manualOverrides.loanAmount && (
              <p className="text-xs text-muted-foreground mt-1">From Monday.com</p>
            )}
            {manualOverrides.loanAmount && (
              <p className="text-xs text-amber-600 mt-1">⚠️ Manual override active</p>
            )}
          </div>
          
          {/* Loan Term */}
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="loanTerm">Loan Term (weeks)</Label>
              {isFromSalesRequest && !manualOverrides.loanTermWeeks && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700"
                  onClick={() => toggleManualOverride('loanTermWeeks')}
                >
                  Manually Enter
                </Button>
              )}
            </div>
            <Input
              id="loanTerm"
              type="number"
              value={loanDetails.loanTermWeeks}
              onChange={(e) => setLoanDetails(prev => ({ ...prev, loanTermWeeks: parseInt(e.target.value) || 26 }))}
              placeholder="26"
              disabled={isFromSalesRequest && !manualOverrides.loanTermWeeks}
              className={isFromSalesRequest && !manualOverrides.loanTermWeeks ? "bg-gray-100 cursor-not-allowed" : ""}
            />
            {isFromSalesRequest && !manualOverrides.loanTermWeeks && (
              <p className="text-xs text-muted-foreground mt-1">From Monday.com</p>
            )}
            {manualOverrides.loanTermWeeks && (
              <p className="text-xs text-amber-600 mt-1">⚠️ Manual override active</p>
            )}
          </div>
          
          {/* Factor Rate */}
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="factorRate">Factor Rate</Label>
              {isFromSalesRequest && !manualOverrides.factorRate && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700"
                  onClick={() => toggleManualOverride('factorRate')}
                >
                  Manually Enter
                </Button>
              )}
            </div>
            <Input
              id="factorRate"
              type="number"
              step="0.01"
              min="1.0"
              max="2.0"
              value={loanDetails.factorRate}
              onChange={(e) => setLoanDetails(prev => ({ ...prev, factorRate: parseFloat(e.target.value) || 1.5 }))}
              placeholder="1.5"
              disabled={isFromSalesRequest && !manualOverrides.factorRate}
              className={isFromSalesRequest && !manualOverrides.factorRate ? "bg-gray-100 cursor-not-allowed" : ""}
            />
            {isFromSalesRequest && !manualOverrides.factorRate ? (
              <p className="text-xs text-muted-foreground mt-1">From Monday.com</p>
            ) : manualOverrides.factorRate ? (
              <p className="text-xs text-amber-600 mt-1">⚠️ Manual override active</p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">Default 1.5 (increments of 0.01)</p>
            )}
          </div>
          
          {/* Admin/Arrangement Fee */}
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="adminFee">Admin/Arrangement Fee (£)</Label>
              {isFromSalesRequest && !manualOverrides.adminFee && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700"
                  onClick={() => toggleManualOverride('adminFee')}
                >
                  Manually Enter
                </Button>
              )}
            </div>
            <Input
              id="adminFee"
              type="number"
              step="0.01"
              min="0"
              value={loanDetails.adminFee}
              onChange={(e) => { setOfferSourced((s) => ({ ...s, adminFee: false })); setLoanDetails(prev => ({ ...prev, adminFee: parseFloat(e.target.value) || 0 })); }}
              placeholder="0.00"
              disabled={isFromSalesRequest && !manualOverrides.adminFee}
              className={isFromSalesRequest && !manualOverrides.adminFee ? "bg-gray-100 cursor-not-allowed" : ""}
            />
            {/* Preset buttons — quick-fill the most common fees */}
            <div className="flex flex-wrap gap-1 mt-1.5">
              {[0, 195, 495, 2995].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    if (isFromSalesRequest && !manualOverrides.adminFee) {
                      toggleManualOverride('adminFee');
                    }
                    setOfferSourced((s) => ({ ...s, adminFee: false }));
                    setLoanDetails(prev => ({ ...prev, adminFee: preset }));
                  }}
                  className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                    loanDetails.adminFee === preset
                      ? "bg-blue-100 border-blue-400 text-blue-800 font-semibold"
                      : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                  }`}
                  data-testid={`admin-fee-preset-${preset}`}
                >
                  £{preset === 0 ? "0" : preset.toLocaleString()}
                </button>
              ))}
            </div>
            {isFromSalesRequest && !manualOverrides.adminFee && (
              <p className="text-xs text-muted-foreground mt-1">From Monday.com</p>
            )}
            {offerSourced.adminFee ? (
              <p className="text-xs text-emerald-600 mt-1" data-testid="admin-fee-offer-source">Pulled from the offer file</p>
            ) : manualOverrides.adminFee ? (
              <p className="text-xs text-amber-600 mt-1">⚠️ Manual override active</p>
            ) : null}
          </div>
          
          {/* Legal Fee */}
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="legalFee">Legal Fee (£)</Label>
              {isFromSalesRequest && !manualOverrides.legalFee && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700"
                  onClick={() => toggleManualOverride('legalFee')}
                >
                  Manually Enter
                </Button>
              )}
            </div>
            <Input
              id="legalFee"
              type="number"
              step="0.01"
              min="0"
              value={loanDetails.legalFee}
              onChange={(e) => { setOfferSourced((s) => ({ ...s, legalFee: false })); setLoanDetails(prev => ({ ...prev, legalFee: parseFloat(e.target.value) || 0 })); }}
              placeholder="0.00"
              disabled={isFromSalesRequest && !manualOverrides.legalFee}
              className={isFromSalesRequest && !manualOverrides.legalFee ? "bg-gray-100 cursor-not-allowed" : ""}
            />
            {/* Preset buttons — quick-fill the most common legal fees */}
            <div className="flex flex-wrap gap-1 mt-1.5">
              {[0, 195, 495, 2995].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    if (isFromSalesRequest && !manualOverrides.legalFee) {
                      toggleManualOverride('legalFee');
                    }
                    setOfferSourced((s) => ({ ...s, legalFee: false }));
                    setLoanDetails(prev => ({ ...prev, legalFee: preset }));
                  }}
                  className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                    loanDetails.legalFee === preset
                      ? "bg-blue-100 border-blue-400 text-blue-800 font-semibold"
                      : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                  }`}
                  data-testid={`legal-fee-preset-${preset}`}
                >
                  £{preset === 0 ? "0" : preset.toLocaleString()}
                </button>
              ))}
            </div>
            {isFromSalesRequest && !manualOverrides.legalFee && (
              <p className="text-xs text-muted-foreground mt-1">From Monday.com</p>
            )}
            {offerSourced.legalFee ? (
              <p className="text-xs text-emerald-600 mt-1" data-testid="legal-fee-offer-source">Pulled from the offer file</p>
            ) : manualOverrides.legalFee ? (
              <p className="text-xs text-amber-600 mt-1">⚠️ Manual override active</p>
            ) : null}
          </div>
          
          {/* Payment Frequency */}
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="paymentFreq">Payment Frequency</Label>
              {isFromSalesRequest && !manualOverrides.paymentFrequency && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700"
                  onClick={() => toggleManualOverride('paymentFrequency')}
                >
                  Manually Enter
                </Button>
              )}
            </div>
            <Select 
              value={loanDetails.paymentFrequency} 
              onValueChange={(value) => setLoanDetails(prev => ({ ...prev, paymentFrequency: value }))}
              disabled={isFromSalesRequest && !manualOverrides.paymentFrequency}
            >
              <SelectTrigger className={isFromSalesRequest && !manualOverrides.paymentFrequency ? "bg-gray-100 cursor-not-allowed" : ""}>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
            {isFromSalesRequest && !manualOverrides.paymentFrequency && (
              <p className="text-xs text-muted-foreground mt-1">From Monday.com</p>
            )}
            {manualOverrides.paymentFrequency && (
              <p className="text-xs text-amber-600 mt-1">⚠️ Manual override active</p>
            )}
          </div>
          
          {/* Payment Day - only shown for weekly payments */}
          {loanDetails.paymentFrequency === "weekly" && (
            <div>
              <Label className="flex items-center gap-1">
                Payment Day
              </Label>
              <Select 
                value={loanDetails.paymentDay} 
                onValueChange={(value) => setLoanDetails(prev => ({ ...prev, paymentDay: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Monday">Monday</SelectItem>
                  <SelectItem value="Tuesday">Tuesday</SelectItem>
                  <SelectItem value="Wednesday">Wednesday</SelectItem>
                  <SelectItem value="Thursday">Thursday</SelectItem>
                  <SelectItem value="Friday">Friday</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Day payments are due</p>
            </div>
          )}

          {/* First payment date — the "commencing on" date in the docs */}
          <div>
            <Label htmlFor="firstPaymentDate">First Payment Date</Label>
            <Input
              id="firstPaymentDate"
              type="date"
              value={loanDetails.firstPaymentDate}
              onChange={(e) => setLoanDetails(prev => ({ ...prev, firstPaymentDate: e.target.value }))}
              data-testid="doc-first-payment-date"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Shown in the docs as "commencing on …" — leave blank to use today's date
            </p>
          </div>
        </div>
        
        {/* Calculated totals preview */}
        {loanDetails.loanAmount > 0 && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Loan Repayment</p>
                <p className="text-lg font-bold text-gray-900">
                  £{(loanDetails.loanAmount * loanDetails.factorRate).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted-foreground">
                  £{loanDetails.loanAmount.toLocaleString()} × {loanDetails.factorRate.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Total Fees</p>
                <p className="text-lg font-bold text-gray-900">
                  £{(loanDetails.adminFee + loanDetails.legalFee).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted-foreground">
                  Admin £{loanDetails.adminFee.toLocaleString()} + Legal £{loanDetails.legalFee.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Total Amount Payable</p>
                <p className="text-lg font-bold text-blue-600">
                  £{((loanDetails.loanAmount * loanDetails.factorRate) + loanDetails.adminFee + loanDetails.legalFee).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {loanDetails.paymentFrequency === "daily" ? (
                    <>Payment: £{(((loanDetails.loanAmount * loanDetails.factorRate) + loanDetails.adminFee + loanDetails.legalFee) / (loanDetails.loanTermWeeks * 5)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/day ({loanDetails.loanTermWeeks * 5} payments)</>
                  ) : (
                    <>Payment: £{(((loanDetails.loanAmount * loanDetails.factorRate) + loanDetails.adminFee + loanDetails.legalFee) / loanDetails.loanTermWeeks).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/week ({loanDetails.loanTermWeeks} payments)</>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Step 2: Company Selection
  const renderStep2CompanySelection = () => (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Select Company</h3>
          {isFromSalesRequest && selectedCompany && !manualOverrides.company && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs text-amber-600 hover:text-amber-700"
              onClick={() => toggleManualOverride('company')}
            >
              Change Company
            </Button>
          )}
        </div>
        
        {/* Company Search - show for manual OR when override is enabled */}
        <div className="space-y-4">
          {(!isFromSalesRequest || manualOverrides.company) && (
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Search company name or number..."
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchCompany(companySearch)}
                />
              </div>
              <Button 
                onClick={() => searchCompany(companySearch)}
                disabled={searchingCompany || companySearch.length < 2}
              >
                {searchingCompany ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
          )}
          
          {/* Manual override warning */}
          {manualOverrides.company && (
            <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
              ⚠️ Manual override active - you can search for a different company
            </div>
          )}
          
          {/* Search Results - Show for both manual and sales (sales needs to confirm company) */}
          {companySearchResults.length > 0 && !selectedCompany && (
            <div className="space-y-2">
              {isFromSalesRequest && !manualOverrides.company && (
                <p className="text-sm text-muted-foreground">
                  Please confirm the correct company from Companies House:
                </p>
              )}
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {companySearchResults.map((company, idx) => (
                  <div 
                    key={idx}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    onClick={() => getCompanyDetails(company.company_number)}
                  >
                    <div className="font-medium">{company.title}</div>
                    <div className="text-sm text-muted-foreground">
                      {company.company_number} • {company.company_status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Selected Company Details */}
          {selectedCompany && companyDetails && (
            <Card className={`border-green-200 ${isFromSalesRequest && !manualOverrides.company ? 'bg-gray-50' : 'bg-green-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-semibold">{companyDetails.company_name}</span>
                      {isFromSalesRequest && !manualOverrides.company && (
                        <Badge variant="secondary" className="ml-2">Locked</Badge>
                      )}
                      {manualOverrides.company && (
                        <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">Override</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      <p>Company Number: {companyDetails.company_number}</p>
                      <p>Status: {companyDetails.company_status}</p>
                      <p>Incorporated: {companyDetails.date_of_creation}</p>
                      {companyDetails.registered_office_address && (
                        <p className="mt-1">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {companyDetails.registered_office_address.address_line_1}, {companyDetails.registered_office_address.locality}, {companyDetails.registered_office_address.postal_code}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Show Change button for manual generation OR when override is active */}
                  {(!isFromSalesRequest || manualOverrides.company) && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedCompany(null);
                        setCompanyDetails(null);
                        setDirectors([]);
                        setCompanySearchResults([]);
                      }}
                    >
                      Change
                    </Button>
                  )}
                </div>
                
                {/* Directors Preview */}
                {directors.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium mb-2">{directors.length} Active Director(s):</p>
                    <div className="flex flex-wrap gap-2">
                      {directors.map((d, idx) => (
                        <Badge key={idx} variant="secondary">{d.name}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Sales request info banner - show auto search status */}
          {isFromSalesRequest && !companyDetails && !loadingCompanyDetails && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                {searchingCompany ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-sm text-blue-800">Searching Companies House for &quot;{companySearch}&quot;...</span>
                  </>
                ) : companySearchResults.length === 0 ? (
                  <>
                    <Search className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-800">
                      Searching for company: <strong>{companySearch}</strong>
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="ml-auto"
                      onClick={() => searchCompany(companySearch)}
                    >
                      Search Now
                    </Button>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-800">
                      Found {companySearchResults.length} result(s) - please select the correct company above
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
          
          {loadingCompanyDetails && (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Loading company details...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Step 3: Director Selection
  const renderStep3DirectorSelection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Select Directors</h3>
        {renderKnownDirectorAlert()}

        <SumsubMatchDialog
          open={!!sumsubDialogDirector}
          onOpenChange={(o) => { if (!o) setSumsubDialogDirector(null); }}
          directorName={sumsubDialogDirector || ""}
          onMatch={(applicant) => applySumsubMatch(sumsubDialogDirector, applicant)}
        />
        
        {/* Signing Directors (multi-select — up to 6; FTLA names the first 3) */}
        <div className="mb-6">
          <Label className="font-medium mb-2 block">Who will be signing on behalf of the company?</Label>
          <p className="text-sm text-muted-foreground mb-3">
            Tick every director who will sign. Up to 6 supported — the Use of Funds and Debenture
            print a signature block for each; the FTLA names the first 3.
          </p>
          <div className="space-y-2" data-testid="signing-directors-checkboxes">
            {directors.map((d, idx) => {
              const checked = signingDirectors.includes(d.name);
              const atLimit = !checked && signingDirectors.length >= 6;
              return (
                <div
                  key={`sd-${idx}`}
                  className={`flex items-center gap-3 border rounded-lg p-3 ${
                    checked ? "border-blue-400 bg-blue-50/40" : "border-muted"
                  } ${atLimit ? "opacity-50" : ""}`}
                >
                  <Checkbox
                    id={`sd-${idx}`}
                    checked={checked}
                    disabled={atLimit}
                    onCheckedChange={() => {
                      setSigningDirectors(prev =>
                        prev.includes(d.name)
                          ? prev.filter(n => n !== d.name)
                          : prev.length < 6 ? [...prev, d.name] : prev
                      );
                    }}
                    data-testid={`signing-director-${idx}`}
                  />
                  <Label htmlFor={`sd-${idx}`} className="font-medium cursor-pointer flex-1">
                    {d.name}
                  </Label>
                </div>
              );
            })}
          </div>
          {signingDirectors.length >= 2 && (
            <p className="text-xs text-blue-700 mt-2">
              ✓ {signingDirectors.length} directors will appear as joint signatories on the FTLA.
            </p>
          )}
        </div>
        
        {/* PG Directors */}
        <div>
          <Label className="font-medium mb-2 block">Select directors for Personal Guarantee</Label>
          <p className="text-sm text-muted-foreground mb-3">
            Check the directors who will provide a personal guarantee
          </p>
          
          <div className="space-y-3">
            {directors.map((director, idx) => (
              <div key={idx} className="border rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id={`pg-${idx}`}
                    checked={pgDirectors.includes(director.name)}
                    onCheckedChange={() => togglePgDirector(director.name)}
                  />
                  <div className="flex-1">
                    <Label htmlFor={`pg-${idx}`} className="font-medium cursor-pointer">
                      {director.name}
                    </Label>
                    {director.date_of_birth && (
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <p className="text-sm text-muted-foreground">
                          DOB (Companies House):
                          {' '}{String(director.date_of_birth.month || '').padStart(2, '0')}/{director.date_of_birth.year}
                        </p>
                        <span className="text-xs text-amber-700">⚠️ Day redacted by CH — enter from ID:</span>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="DD"
                          value={directorDobDays[director.name] || ""}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                            const n = parseInt(raw, 10);
                            if (raw === "" || (n >= 1 && n <= 31)) {
                              setDirectorDobDays(prev => ({ ...prev, [director.name]: raw }));
                            }
                          }}
                          className="w-20 h-8 text-sm"
                          data-testid={`dob-day-${idx}`}
                        />
                        {directorDobDays[director.name] && (
                          <span className="text-xs text-green-700">
                            → {String(directorDobDays[director.name]).padStart(2, '0')}/
                            {String(director.date_of_birth.month || '').padStart(2, '0')}/
                            {director.date_of_birth.year}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Address input for PG directors with UK postcode lookup via Ideal Postcodes */}
                    {pgDirectors.includes(director.name) && (
                      <div className="mt-3 space-y-2">
                        {/* Sumsub ID check match — pulls DOB/address/phone/email */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSumsubDialogDirector(director.name)}
                            data-testid={`find-sumsub-${idx}`}
                          >
                            <Fingerprint className="w-4 h-4 mr-1.5 text-indigo-600" />
                            Find Sumsub ID check
                          </Button>
                          {sumsubMatches[director.name] && (
                            <span className={`text-xs font-medium ${
                              sumsubMatches[director.name].review_answer === "GREEN" ? "text-green-700"
                                : sumsubMatches[director.name].review_answer === "RED" ? "text-red-600"
                                : "text-amber-700"}`}>
                              ✓ Matched: {sumsubMatches[director.name].full_name}
                              {sumsubMatches[director.name].dob ? ` · DOB ${sumsubMatches[director.name].dob}` : ""}
                              {sumsubMatches[director.name].review_answer ? ` · ${sumsubMatches[director.name].review_answer}` : ""}
                            </span>
                          )}
                        </div>
                        <Label className="text-sm">Personal/Residential Address</Label>
                        
                        {/* UK Postcode Search using Ideal Postcodes */}
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Input
                              placeholder="Enter UK postcode (e.g., SW1A 1AA)..."
                              value={activeAddressDirector === director.name ? addressSearchQuery : ""}
                              onChange={(e) => {
                                setActiveAddressDirector(director.name);
                                setAddressSearchQuery(e.target.value.toUpperCase());
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  searchAddress(addressSearchQuery);
                                }
                              }}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setActiveAddressDirector(director.name);
                              searchAddress(addressSearchQuery);
                            }}
                            disabled={searchingAddress || !addressSearchQuery || addressSearchQuery.length < 3}
                          >
                            {searchingAddress && activeAddressDirector === director.name ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>Find Address</>
                            )}
                          </Button>
                        </div>
                        
                        {/* Address Dropdown - all addresses in the postcode */}
                        {activeAddressDirector === director.name && addressSearchResults.length > 0 && (
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              {addressSearchResults.length} address(es) found - select one:
                            </Label>
                            <Select
                              onValueChange={(value) => {
                                setDirectorAddresses(prev => ({
                                  ...prev,
                                  [director.name]: value
                                }));
                                setAddressSearchResults([]);
                                setAddressSearchQuery("");
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select address..." />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {addressSearchResults.map((result, addrIdx) => (
                                  <SelectItem key={addrIdx} value={result.fullAddress}>
                                    {result.address}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {/* Manual Address Entry */}
                        <Input
                          placeholder="Or enter full address manually..."
                          value={directorAddresses[director.name] || ""}
                          onChange={(e) => setDirectorAddresses(prev => ({
                            ...prev,
                            [director.name]: e.target.value
                          }))}
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground">
                          Search by UK postcode or enter the full address manually
                        </p>
                        
                        {/* Phone and Email for PG */}
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div>
                            <Label className="text-sm">Telephone</Label>
                            <Input
                              placeholder="e.g., 07700 900123"
                              value={directorPhones[director.name] || ""}
                              onChange={(e) => setDirectorPhones(prev => ({
                                ...prev,
                                [director.name]: e.target.value
                              }))}
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Email</Label>
                            <Input
                              type="email"
                              placeholder="e.g., name@email.com"
                              value={directorEmails[director.name] || ""}
                              onChange={(e) => setDirectorEmails(prev => ({
                                ...prev,
                                [director.name]: e.target.value
                              }))}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Manual PG Guarantors Section - for non-directors */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-md font-medium">Additional Personal Guarantors</h4>
                <p className="text-sm text-muted-foreground">
                  Add third-party guarantors who are not directors (max 6 total PGs — each gets their own deed)
                </p>
              </div>
              {(pgDirectors.length + manualPgGuarantors.length) < 6 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setManualPgGuarantors(prev => [...prev, {
                      id: `manual_pg_${Date.now()}`,
                      name: '',
                      address: '',
                      phone: '',
                      email: '',
                      dob: ''
                    }]);
                  }}
                  data-testid="add-manual-pg-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Third Party PG
                </Button>
              )}
            </div>
            
            {/* Manual PG List */}
            {manualPgGuarantors.length > 0 && (
              <div className="space-y-4">
                {manualPgGuarantors.map((pg, idx) => (
                  <Card key={pg.id} className="border-purple-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">
                          Third Party PG {idx + 1}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setManualPgGuarantors(prev => prev.filter(p => p.id !== pg.id))}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm">Full Name *</Label>
                          <Input
                            placeholder="Enter full name..."
                            data-testid={`manual-pg-name-${idx}`}
                            value={pg.name}
                            onChange={(e) => setManualPgGuarantors(prev => 
                              prev.map(p => p.id === pg.id ? {...p, name: e.target.value} : p)
                            )}
                            onBlur={(e) => runDirectorCrosscheck([e.target.value])}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm">Residential Address *</Label>
                          <Input
                            placeholder="Full address..."
                            data-testid={`manual-pg-address-${idx}`}
                            value={pg.address}
                            onChange={(e) => setManualPgGuarantors(prev => 
                              prev.map(p => p.id === pg.id ? {...p, address: e.target.value} : p)
                            )}
                          />
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-sm">Telephone</Label>
                            <Input
                              placeholder="Phone number"
                              value={pg.phone}
                              onChange={(e) => setManualPgGuarantors(prev => 
                                prev.map(p => p.id === pg.id ? {...p, phone: e.target.value} : p)
                              )}
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Email *</Label>
                            <Input
                              type="email"
                              placeholder="Email address"
                              data-testid={`manual-pg-email-${idx}`}
                              value={pg.email}
                              onChange={(e) => setManualPgGuarantors(prev => 
                                prev.map(p => p.id === pg.id ? {...p, email: e.target.value} : p)
                              )}
                            />
                            {(() => {
                              const em = (pg.email || "").trim().toLowerCase();
                              const dup = em && directors.find(d => (directorEmails[d.name] || d.email || "").trim().toLowerCase() === em);
                              return dup ? (
                                <p className="mt-1 text-[11px] text-amber-700" data-testid={`manual-pg-dup-director-${idx}`}>
                                  Same email as director <b>{dup.name}</b> — they'll get ONE signing session (director + guarantor) under the fuller name. Prefer ticking them in the director PG list instead of adding manually.
                                </p>
                              ) : null;
                            })()}
                          </div>
                          <div>
                            <Label className="text-sm">Date of Birth</Label>
                            <Input
                              placeholder="DD/MM/YYYY"
                              value={pg.dob}
                              onChange={(e) => setManualPgGuarantors(prev => 
                                prev.map(p => p.id === pg.id ? {...p, dob: e.target.value} : p)
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            {(pgDirectors.length + manualPgGuarantors.length) >= 6 && (
              <p className="text-sm text-amber-600 mt-2">
                Maximum 6 personal guarantors reached
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // CoP verification state
  const [copStatus, setCopStatus] = useState(null); // null, "verifying", "verified", "partial", "no_match", "error"
  const [copResult, setCopResult] = useState(null);
  
  // Run CoP check
  const runCoPCheck = async () => {
    if (!bankDetails.sortCode || !bankDetails.accountNumber) {
      toast.error("Sort code and account number are required");
      return;
    }
    
    const cleanSortCode = bankDetails.sortCode.replace(/-/g, '');
    if (cleanSortCode.length !== 6) {
      toast.error("Sort code must be 6 digits");
      return;
    }
    
    if (bankDetails.accountNumber.length !== 8) {
      toast.error("Account number must be 8 digits");
      return;
    }
    
    try {
      setCopStatus("verifying");
      
      const response = await api.post(`/contracts/bank-details/${selectedRequest?.application_id || 'MANUAL'}/cop-check`, {
        account_number: bankDetails.accountNumber,
        sort_code: bankDetails.sortCode,
        account_name: bankDetails.accountName || companyDetails?.company_name || ""
      });
      
      setCopResult(response.data);
      
      if (response.data.cop_verified) {
        setCopStatus("verified");
        setBankDetails(prev => ({
          ...prev,
          ...response.data.bank_details,
          verified: true,
          verifiedBy: response.data.provider,
          verifiedAt: new Date().toISOString()
        }));
        setBankDetailsSource("cop_verified");
        toast.success("Bank details verified via Confirmation of Payee");
      } else if (response.data.credit_review_required) {
        // A full match on a change of bank is a note for Credit, not a verification: Credit approves it.
        setCopStatus("credit");
        setBankDetailsSource("credit_approval");
        toast.info(response.data.message || "Bank details will be sent to Credit for approval");
      } else if (response.data.match_status === "partial_match") {
        setCopStatus("partial");
        toast.warning("Partial match - name mismatch detected");
      } else if (response.data.match_status === "no_match") {
        setCopStatus("no_match");
        toast.error("No match - bank details do not match company");
      } else {
        setCopStatus("error");
        toast.error("CoP check failed");
      }
    } catch (error) {
      console.error("CoP check error:", error);
      setCopStatus("error");
      toast.error(error.response?.data?.detail || "Failed to verify bank details");
    }
  };

  // Step 4: Bank Details (CoP Verification Required)
  const renderStep4BankDetails = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Customer Bank Details</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Bank details <strong>must be verified via Confirmation of Payee (CoP)</strong> before documents can be generated.
        </p>
        
        {/* Loading state */}
        {loadingBankDetails && (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Checking for existing CoP verification...</span>
          </div>
        )}
        
        {/* Already CoP Verified from Stage 2b */}
        {!loadingBankDetails && bankDetailsSource === "stage_2b" && bankDetails.verified && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              <div className="flex-1">
                <span className="font-medium">Bank details already CoP verified</span>
                <p className="text-sm text-green-700">
                  Verified by Sales team in Stage 2b via {bankDetails.verifiedBy === "creditsafe" ? "Creditsafe" : bankDetails.verifiedBy}
                  {bankDetails.verifiedAt && ` on ${new Date(bankDetails.verifiedAt).toLocaleDateString("en-GB")}`}
                </p>
              </div>
              <Badge variant="secondary" className="bg-green-100 text-green-800">Passed to Legal</Badge>
            </div>
          </div>
        )}
        
        {/* CoP Verification in Progress */}
        {copStatus === "verifying" && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-blue-800">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="font-medium">Running Confirmation of Payee check...</span>
            </div>
          </div>
        )}
        
        {/* CoP Verified Successfully */}
        {copStatus === "verified" && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              <div>
                <span className="font-medium">CoP Verification Successful</span>
                <p className="text-sm text-green-700">
                  Bank account verified via {copResult?.provider === "mock" ? "Mock (Testing)" : "Creditsafe"}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Sent to Credit instead of verified */}
        {copStatus === "credit" && (
          <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertCircle className="w-5 h-5" />
              <div>
                <span className="font-medium">Not verified - going to Credit</span>
                <p className="text-sm text-amber-700">
                  Credit approves these bank details before the account can be paid. They are queued for Credit approval when the documents are generated.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* CoP Partial Match */}
        {copStatus === "partial" && copResult && (
          <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg mb-4">
            <div className="flex items-start gap-2 text-amber-800">
              <AlertCircle className="w-5 h-5 mt-0.5" />
              <div className="flex-1">
                <span className="font-medium">Partial Match - Name Mismatch</span>
                <p className="text-sm text-amber-700 mt-1">
                  The account holder name does not exactly match the company name.
                </p>
                <div className="mt-2 text-sm">
                  <p><strong>Submitted:</strong> {copResult.submitted_name}</p>
                  {copResult.returned_name && <p><strong>Bank returned:</strong> {copResult.returned_name}</p>}
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  This will be flagged for Credit review. You can still proceed with document generation.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* CoP No Match */}
        {copStatus === "no_match" && (
          <div className="p-4 bg-red-50 border border-red-300 rounded-lg mb-4">
            <div className="flex items-start gap-2 text-red-800">
              <XCircle className="w-5 h-5 mt-0.5" />
              <div>
                <span className="font-medium">No Match Found</span>
                <p className="text-sm text-red-700 mt-1">
                  Bank details do not match. Please verify the sort code, account number, and account name are correct.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* CoP Error */}
        {copStatus === "error" && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-red-800">
              <XCircle className="w-5 h-5" />
              <span className="font-medium">CoP check failed - please try again</span>
            </div>
          </div>
        )}
        
        {/* Bank details form - only show if not already verified from Stage 2b */}
        {!loadingBankDetails && (
          <div className="space-y-4">
            {/* Show read-only if verified from Stage 2b */}
            {bankDetailsSource === "stage_2b" && bankDetails.verified ? (
              <div className="p-4 bg-gray-50 border rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Sort Code</Label>
                    <p className="font-medium">{bankDetails.sortCode}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Account Number</Label>
                    <p className="font-medium">{bankDetails.accountNumber}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Account Name</Label>
                    <p className="font-medium">{bankDetails.accountName}</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Editable form for new CoP verification */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sortCode">Sort Code *</Label>
                    <Input
                      id="sortCode"
                      placeholder="00-00-00"
                      value={bankDetails.sortCode}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length > 6) value = value.slice(0, 6);
                        if (value.length >= 4) {
                          value = value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4);
                        } else if (value.length >= 2) {
                          value = value.slice(0, 2) + '-' + value.slice(2);
                        }
                        setBankDetails(prev => ({ ...prev, sortCode: value, verified: false }));
                        setCopStatus(null);
                      }}
                      disabled={copStatus === "verifying" || copStatus === "verified" || copStatus === "credit"}
                      className={copStatus === "verified" || copStatus === "credit" ? "bg-gray-100" : ""}
                    />
                  </div>
                  <div>
                    <Label htmlFor="accountNumber">Account Number *</Label>
                    <Input
                      id="accountNumber"
                      placeholder="12345678"
                      maxLength={8}
                      value={bankDetails.accountNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                        setBankDetails(prev => ({ ...prev, accountNumber: value, verified: false }));
                        setCopStatus(null);
                      }}
                      disabled={copStatus === "verifying" || copStatus === "verified" || copStatus === "credit"}
                      className={copStatus === "verified" || copStatus === "credit" ? "bg-gray-100" : ""}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="accountName">Account Name *</Label>
                  <Input
                    id="accountName"
                    placeholder="Must match name on bank account"
                    value={bankDetails.accountName || companyDetails?.company_name || ""}
                    onChange={(e) => {
                      setBankDetails(prev => ({ ...prev, accountName: e.target.value, verified: false }));
                      setCopStatus(null);
                    }}
                    disabled={copStatus === "verifying" || copStatus === "verified" || copStatus === "credit"}
                    className={copStatus === "verified" || copStatus === "credit" ? "bg-gray-100" : ""}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This must match the name registered with the bank
                  </p>
                </div>
                
                {/* CoP Verification Button */}
                {copStatus !== "verified" && copStatus !== "credit" && (
                  <div className="pt-4 border-t">
                    <Button 
                      onClick={runCoPCheck}
                      disabled={
                        copStatus === "verifying" ||
                        !bankDetails.sortCode || 
                        !bankDetails.accountNumber || 
                        bankDetails.sortCode.replace(/-/g, '').length < 6 ||
                        bankDetails.accountNumber.length < 8
                      }
                      className="w-full"
                    >
                      {copStatus === "verifying" ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Run Confirmation of Payee Check
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      CoP verification is required before generating documents
                    </p>
                    
                    {/* The server decides whether details are verified, from the stored check for these digits.
                        This button only lets the wizard continue; the details go to Credit for approval. */}
                    <div className="mt-4 pt-4 border-t border-dashed">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setCopStatus("credit");
                          setBankDetailsSource("credit_approval");
                          toast.info("Bank details will be sent to Credit for approval when the documents are generated");
                        }}
                        disabled={
                          copStatus === "verifying" ||
                          (bankDetails.sortCode || "").replace(/\D/g, "").length !== 6 ||
                          (bankDetails.accountNumber || "").replace(/\D/g, "").length !== 8
                        }
                        className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      >
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Send to Credit for approval
                      </Button>
                      <p className="text-xs text-amber-600 mt-2 text-center">
                        Use this if CoP check is unavailable or returns no match.
                        The details are not verified: Credit checks and approves them.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Verified - show confirmation + edit unlock */}
                {(copStatus === "verified" || copStatus === "credit") && (
                  <div className="pt-4 border-t text-center space-y-3">
                    <p className={`text-sm font-medium ${copStatus === "credit" ? "text-amber-700" : "text-green-700"}`}>
                      {copStatus === "credit"
                        ? "Bank details not verified - they will be sent to Credit for approval"
                        : "✓ Bank details verified and saved to application"}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Unlock the form so the operator can correct the values.
                        // Clears verified state — user must re-run CoP or send to Credit again.
                        setCopStatus(null);
                        setCopResult(null);
                        setBankDetails(prev => ({
                          ...prev,
                          verified: false,
                          verifiedBy: null,
                          verifiedAt: null,
                        }));
                        setBankDetailsSource("pending");
                        toast.info("Bank details unlocked — please re-verify after editing.");
                      }}
                      data-testid="edit-bank-details-btn"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit bank details
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Click to unlock the form (e.g. if the values are wrong or were never entered).
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Add a cross company to the list
  const addCrossCompanyToList = () => {
    if (!selectedCrossCompany || !crossCompanyDetails || !crossCompanySigningDirector) {
      toast.error("Please select a company and signing director");
      return;
    }
    
    if (crossCompanies.length >= 6) {
      toast.error("Maximum 6 cross company guarantees allowed");
      return;
    }
    
    // Check if already added
    if (crossCompanies.find(c => c.company_number === selectedCrossCompany.company_number)) {
      toast.error("This company is already added");
      return;
    }
    
    const newCrossCompany = {
      company_number: selectedCrossCompany.company_number,
      company_name: crossCompanyDetails.company_name,
      registered_address: crossCompanyDetails.registered_office_address 
        ? `${crossCompanyDetails.registered_office_address.address_line_1 || ''}, ${crossCompanyDetails.registered_office_address.locality || ''}, ${crossCompanyDetails.registered_office_address.postal_code || ''}`.trim().replace(/^,|,$/g, '')
        : '',
      signing_director_name: crossCompanySigningDirector,
      officer_names: (crossCompanyDetails.directors || []).map(d => d.name),
    };
    
    setCrossCompanies(prev => [...prev, newCrossCompany]);
    
    // Reset search for next company
    setSelectedCrossCompany(null);
    setCrossCompanyDetails(null);
    setCrossCompanySearch("");
    setCrossCompanySigningDirector(null);
    setCrossCompanyResults([]);
    
    toast.success(`Added ${newCrossCompany.company_name} as Cross Company Guarantee`);
  };
  
  // Remove a cross company from the list
  const removeCrossCompany = (companyNumber) => {
    setCrossCompanies(prev => prev.filter(c => c.company_number !== companyNumber));
    setPgCompanyLinks(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, v.filter(n => n !== companyNumber)])));
  };

  // Companies House officer names are "SURNAME, First Middle" — compare on sorted tokens
  const nameTokens = (n) => (n || "").toUpperCase().replace(/[^A-Z\s]/g, " ").split(/\s+/).filter(Boolean).sort().join(" ");
  const isOfficerOf = (guarantorName, cc) => {
    const g = nameTokens(guarantorName);
    if (!g) return false;
    return (cc.officer_names || []).some(o => {
      const ot = nameTokens(o);
      if (ot === g) return true;
      const gs = new Set(g.split(" ")), os = ot.split(" ");
      const overlap = os.filter(t => gs.has(t)).length;
      return overlap >= 2 && overlap >= Math.min(gs.size, os.length);
    });
  };
  const allPgNames = [...pgDirectors, ...manualPgGuarantors.map(pg => pg.name).filter(Boolean)];
  const isPgLinked = (name, companyNumber) => (pgCompanyLinks[name] || []).includes(companyNumber);
  const togglePgLink = (name, companyNumber) => {
    setPgLinkTouched(prev => ({ ...prev, [`${name}|${companyNumber}`]: true }));
    setPgCompanyLinks(prev => {
      const cur = prev[name] || [];
      return { ...prev, [name]: cur.includes(companyNumber) ? cur.filter(n => n !== companyNumber) : [...cur, companyNumber] };
    });
  };
  // Auto-seed links from Companies House officers (never overrides a manual toggle)
  useEffect(() => {
    if (contractTypes.guaranteeType !== "pg_cross_company" || crossCompanies.length === 0) return;
    setPgCompanyLinks(prev => {
      const next = { ...prev };
      let changed = false;
      for (const name of allPgNames) {
        const cur = next[name] || [];
        const add = crossCompanies
          .filter(cc => !pgLinkTouched[`${name}|${cc.company_number}`] && !cur.includes(cc.company_number) && isOfficerOf(name, cc))
          .map(cc => cc.company_number);
        if (add.length) { next[name] = [...cur, ...add]; changed = true; }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossCompanies, pgDirectors, manualPgGuarantors, contractTypes.guaranteeType]);

  // Step 5: Cross Company (if applicable) - now supports multiple (up to 6)
  // Lender signatory selector — shared by Step 5 (printed on the docs) and the
  // Step 6 SF card (DocuSign routing). Non-Dean signers get the POA line.
  const renderLenderSignatoryCard = () => (
    <Card className="border-blue-200 bg-blue-50/50" data-testid="uk-lender-signatory-card">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <div>
            <CardTitle className="text-sm">Lender Signatory (SimplyFunded)</CardTitle>
            <p className="text-xs text-muted-foreground">Printed on the FTLA, PG and Debenture lender signature blocks</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs text-muted-foreground">Who will sign on behalf of the Lender?</Label>
          <Select
            value={lenderSignerType}
            onValueChange={(val) => {
              setLenderSignerType(val);
              if (val === "dean_morley") {
                setSfSigner({ name: "Dean Morley", email: "dean.morley@simplyfunded.co.uk", title: "Managing Director" });
              } else if (val === "me") {
                setSfSigner({
                  name: user?.name || "",
                  email: user?.email || "",
                  title: "Director",
                });
              } else {
                setSfSigner({ name: "", email: "", title: "" });
              }
            }}
          >
            <SelectTrigger data-testid="uk-lender-signer-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dean_morley" data-testid="uk-lender-signer-dean">Dean Morley (Managing Director)</SelectItem>
              <SelectItem value="me" data-testid="uk-lender-signer-me">Me (my portal account)</SelectItem>
              <SelectItem value="other" data-testid="uk-lender-signer-other">Someone else (enter manually)</SelectItem>
            </SelectContent>
          </Select>
          {lenderSignerType !== "dean_morley" && (
            <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              <strong>"Authorised Signatory under Power Of Attorney"</strong> will be added beneath the title on the signature block.
            </p>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Name *</Label>
            <Input value={sfSigner.name} disabled={lenderSignerType === "dean_morley"}
              onChange={(e) => setSfSigner(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Dean Morley" data-testid="uk-lender-signer-name" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Email *</Label>
            <Input value={sfSigner.email} disabled={lenderSignerType === "dean_morley"}
              onChange={(e) => setSfSigner(prev => ({ ...prev, email: e.target.value }))}
              placeholder="name@simplyfunded.co.uk" data-testid="uk-lender-signer-email" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Title *</Label>
            <Input value={sfSigner.title} disabled={lenderSignerType === "dean_morley"}
              onChange={(e) => setSfSigner(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Director" data-testid="uk-lender-signer-title" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep5CrossCompany = () => {
    if (contractTypes.guaranteeType !== "pg_cross_company") {
      // Skip this step
      return (
        <div className="space-y-4">
          {renderLenderSignatoryCard()}
          {contractTypes.useOfFunds && (
            <UseOfFundsForm
              loanAmount={loanDetails.loanAmount}
              arrangementFee={loanDetails.adminFee}
              breakdown={useOfFundsBreakdown}
              onChange={setUseOfFundsBreakdown}
              prefilledSummary={useOfFundsPrefill}
              companyName={companyDetails?.company_name || selectedCompany?.title || companySearch || ""}
            />
          )}
          <div className="text-center py-4">
            <p className="text-muted-foreground mb-3">
              {contractTypes.useOfFunds
                ? "Review the Use of Funds breakdown above, then generate."
                : "Cross company guarantee not selected."}
            </p>
            <Button
              onClick={() => {
                generateDocuments();
              }}
              data-testid="generate-all-docs-btn"
            >
              Generate All Documents
            </Button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        {renderLenderSignatoryCard()}
        <div>
          <h3 className="text-lg font-semibold mb-2">Cross Company Guarantees</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Add up to 6 cross-company guarantors. Find each guarantor's companies on Companies House below, or search a company manually.
          </p>

          {allPgNames.length > 0 && (
            <GuarantorCompaniesFinder
              guarantorNames={allPgNames}
              directors={directors}
              borrowerCompanyNumber={selectedCompany?.company_number || companyDetails?.company_number}
              crossCompanies={crossCompanies}
              onAdd={(guarantor, companies) => {
                setCrossCompanies(prev => [...prev, ...companies.filter(c => !prev.some(p => p.company_number === c.company_number))]);
                setPgCompanyLinks(prev => ({ ...prev, [guarantor]: [...new Set([...(prev[guarantor] || []), ...companies.map(c => c.company_number)])] }));
                setPgLinkTouched(prev => ({ ...prev, ...Object.fromEntries(companies.map(c => [`${guarantor}|${c.company_number}`, true])) }));
              }}
            />
          )}
          
          {/* Already Added Cross Companies */}
          {crossCompanies.length > 0 && (
            <div className="mb-4 space-y-2">
              <Label className="text-sm font-medium">Added Cross Companies ({crossCompanies.length}/6)</Label>
              {crossCompanies.map((cc, idx) => (
                <Card key={cc.company_number} className="border-purple-200 bg-purple-50">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-purple-100">{idx + 1}</Badge>
                          <span className="font-medium">{cc.company_name}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {cc.company_number} • Signing: {cc.signing_director_name}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeCrossCompany(cc.company_number)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* PG ↔ company matrix — which cross companies print on each guarantor's deed */}
              {allPgNames.length > 0 && (
                <Card className="border-indigo-200" data-testid="pg-company-matrix">
                  <CardContent className="p-3">
                    <p className="text-sm font-medium">Companies listed on each Personal Guarantee</p>
                    <p className="text-xs text-muted-foreground mb-2">
                      Pre-ticked when the guarantor is a Companies House officer of that company. Un-tick to keep a company off
                      their deed; a guarantor with nothing ticked gets a standard PG instead.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="text-left text-muted-foreground">
                            <th className="py-1 pr-3 font-medium">Guarantor</th>
                            {crossCompanies.map(cc => (
                              <th key={cc.company_number} className="py-1 px-2 font-medium max-w-[9rem] truncate" title={cc.company_name}>{cc.company_name}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {allPgNames.map(name => {
                            const linked = (pgCompanyLinks[name] || []).filter(n => crossCompanies.some(cc => cc.company_number === n));
                            return (
                              <tr key={name} className="border-t" data-testid={`pg-company-row-${name}`}>
                                <td className="py-1.5 pr-3">
                                  <span className="font-medium">{name}</span>
                                  {linked.length === 0 && (
                                    <Badge variant="outline" className="ml-2 bg-slate-50 text-slate-600" data-testid={`pg-standard-badge-${name}`}>standard PG</Badge>
                                  )}
                                </td>
                                {crossCompanies.map(cc => {
                                  const officer = isOfficerOf(name, cc);
                                  return (
                                    <td key={cc.company_number} className="py-1.5 px-2">
                                      <label className="inline-flex items-center gap-1 cursor-pointer" title={officer ? "Companies House officer of this company" : "Not a Companies House officer of this company"}>
                                        <Checkbox
                                          checked={isPgLinked(name, cc.company_number)}
                                          onCheckedChange={() => togglePgLink(name, cc.company_number)}
                                          data-testid={`pg-link-${name}-${cc.company_number}`}
                                        />
                                        {officer && <span className="text-[10px] text-green-700">CH officer</span>}
                                      </label>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          {/* Add New Cross Company - only show if under limit */}
          {crossCompanies.length < 6 && (
            <Card className="border-dashed">
              <CardContent className="p-4">
                <Label className="text-sm font-medium mb-2 block">
                  {crossCompanies.length === 0 ? "Add Cross Company Guarantee" : "Add Another Cross Company"}
                </Label>
                
                {/* Cross Company Search */}
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Search company by name or number..."
                        value={crossCompanySearch}
                        onChange={(e) => setCrossCompanySearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchCompany(crossCompanySearch, true)}
                      />
                    </div>
                    <Button 
                      onClick={() => searchCompany(crossCompanySearch, true)}
                      disabled={searchingCrossCompany || crossCompanySearch.length < 2}
                    >
                      {searchingCrossCompany ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                  
                  {/* Search Results */}
                  {crossCompanyResults.length > 0 && !selectedCrossCompany && (
                    <div className="border rounded-lg max-h-48 overflow-y-auto">
                      {crossCompanyResults.map((company, idx) => (
                        <div 
                          key={idx}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => getCompanyDetails(company.company_number, true)}
                        >
                          <div className="font-medium">{company.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {company.company_number} • {company.company_status}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Selected Cross Company */}
                  {selectedCrossCompany && crossCompanyDetails && (
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="font-medium">{crossCompanyDetails.company_name}</span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {crossCompanyDetails.company_number}
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedCrossCompany(null);
                            setCrossCompanyDetails(null);
                            setCrossCompanySigningDirector(null);
                          }}
                        >
                          Change
                        </Button>
                      </div>
                      
                      {/* Signing Director Select */}
                      <div className="space-y-2">
                        <Label className="text-sm">Signing Director *</Label>
                        <Select value={crossCompanySigningDirector || ""} onValueChange={setCrossCompanySigningDirector}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select signing director" />
                          </SelectTrigger>
                          <SelectContent>
                            {crossCompanyDetails.directors?.map((d, idx) => (
                              <SelectItem key={idx} value={d.name}>{d.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Add Button */}
                      <Button 
                        className="w-full mt-3"
                        onClick={addCrossCompanyToList}
                        disabled={!crossCompanySigningDirector}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Cross Company
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {crossCompanies.length >= 6 && (
            <p className="text-sm text-amber-600 text-center">
              Maximum 6 cross company guarantees reached
            </p>
          )}

          {/* Use of Funds breakdown — shown alongside cross-company config when flagged */}
          {contractTypes.useOfFunds && (
            <UseOfFundsForm
              loanAmount={loanDetails.loanAmount}
              arrangementFee={loanDetails.adminFee}
              breakdown={useOfFundsBreakdown}
              onChange={setUseOfFundsBreakdown}
              prefilledSummary={useOfFundsPrefill}
              companyName={companyDetails?.company_name || selectedCompany?.title || companySearch || ""}
            />
          )}
        </div>
      </div>
    );
  };

  // Step 6: Results - was Step 5
  const renderStep6Results = () => (
    <div className="space-y-6">
      {/* Generated Documents */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Generated Documents</h3>
        
        {generatedDocs.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              These will be added to the DocuSign envelope top-to-bottom — use the arrows to re-order,
              the eye to preview, or upload extra documents below.
            </p>
            {generatedDocs.map((doc, idx) => (
              <Card key={idx} data-testid={`generated-doc-${idx}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex flex-col">
                        <button type="button" onClick={() => moveGeneratedDoc(idx, -1)} disabled={idx === 0}
                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30" data-testid={`doc-up-${idx}`}>
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => moveGeneratedDoc(idx, 1)} disabled={idx === generatedDocs.length - 1}
                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30" data-testid={`doc-down-${idx}`}>
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="text-xs font-mono text-muted-foreground w-4 shrink-0">{idx + 1}.</span>
                      <FileText className="w-6 h-6 text-blue-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{doc.type}</p>
                        {doc.cross_companies?.length > 0 && (
                          <p className="text-[11px] text-indigo-700 truncate" data-testid={`doc-companies-${idx}`}>Companies: {doc.cross_companies.join(", ")}</p>
                        )}
                        <p className="text-xs text-muted-foreground truncate">
                          {doc.custom_upload ? `Uploaded: ${doc.filename}` : doc.replaced ? `✎ Replaced with ${doc.filename}` : (doc.result?.file_name || "Document generated")}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {(doc.result?.file_url || doc.file_base64) && (
                        <Button variant="outline" size="sm" onClick={() => previewDoc(doc)} data-testid={`doc-view-${idx}`}>
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                      )}
                      {doc.result?.file_url && !doc.replaced && (
                        <Button size="sm" asChild>
                          <a href={doc.result.file_url} download>
                            <Download className="w-4 h-4 mr-1" /> PDF
                          </a>
                        </Button>
                      )}
                      {doc.result?.file_url && !doc.replaced && (
                        <Button size="sm" variant="outline" onClick={() => downloadDocAsWord(doc)} disabled={wordBusy === idx}
                          title="Download as an editable Word file to re-format before DocuSign" data-testid={`doc-word-${idx}`}>
                          {wordBusy === idx ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Word
                        </Button>
                      )}
                      {!doc.custom_upload && (
                        <label className="inline-flex items-center h-9 px-3 rounded-md border text-xs cursor-pointer hover:bg-slate-50"
                          title="Replace this document with your edited PDF/Word file — the replacement goes to DocuSign" data-testid={`doc-replace-${idx}`}>
                          <Upload className="w-4 h-4 mr-1" /> {doc.replaced ? "Replace again" : "Replace"}
                          <input type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            className="hidden" onChange={(e) => handleReplaceDoc(idx, e)} data-testid={`doc-replace-input-${idx}`} />
                        </label>
                      )}
                      {doc.replaced && (
                        <Button variant="ghost" size="sm" className="text-slate-500" title="Revert to the generated document"
                          onClick={() => setGeneratedDocs(prev => prev.map((d, i) => i === idx ? { ...d, replaced: false, file_base64: undefined, filename: undefined, content_type: undefined } : d))}
                          data-testid={`doc-revert-${idx}`}>
                          Revert
                        </Button>
                      )}
                      {doc.custom_upload && (
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                          onClick={() => setGeneratedDocs(prev => prev.filter((_, i) => i !== idx))}
                          data-testid={`doc-remove-${idx}`}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            <label className="flex items-center gap-2 border border-dashed rounded-lg p-3 cursor-pointer hover:bg-slate-50 text-sm text-muted-foreground w-fit">
              <Upload className="w-4 h-4" />
              Upload additional document(s) to the envelope
              <input type="file" accept=".pdf,application/pdf" multiple className="hidden" onChange={handleExtraDocUpload}
                data-testid="upload-extra-doc-input" />
            </label>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
            <p>No documents were generated. Please check the errors and try again.</p>
          </div>
        )}
      </div>
      
      {/* DocuSign Section */}
      {generatedDocs.length > 0 && !docuSignResult && (
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Send for E-Signature (DocuSign)
          </h3>
          
          <Card className="bg-blue-50 border-blue-200 mb-4">
            <CardContent className="p-4">
              <p className="text-sm text-blue-800">
                <strong>{generatedDocs.length} document(s)</strong> will be combined into a single DocuSign envelope. 
                Configure signers and witnesses below.
              </p>
            </CardContent>
          </Card>
          
          {/* ====== CUSTOMER SIGNERS SECTION ====== */}
          <div className="space-y-4 mb-6">
            <h4 className="font-medium text-sm text-muted-foreground border-b pb-2">Customer Signers</h4>
            
            {/* Company Signing Director (for FTLA/Debenture) */}
            {signingDirector && (
              <div className="p-4 border rounded-lg bg-white space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{signingDirector}</span>
                    <Badge variant="secondary">Company Signing Director</Badge>
                  </div>
                </div>
                
                {/* Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Email Address</Label>
                    <Input
                      type="email"
                      placeholder="Enter email..."
                      value={signerConfig[`company_${signingDirector}`]?.email || 
                             directorEmails[signingDirector] ||
                             directors.find(d => d.name === signingDirector)?.email || ""}
                      onChange={(e) => setSignerConfig(prev => ({
                        ...prev,
                        [`company_${signingDirector}`]: {
                          ...prev[`company_${signingDirector}`],
                          name: signingDirector,
                          role: 'signing_director',
                          email: e.target.value
                        }
                      }))}
                      data-testid="company-signer-email"
                    />
                    <SumsubUseButton name={signingDirector} testid="sumsub-fill-company-signer"
                      onFill={(a) => {
                        if (a.email) setSignerConfig(prev => ({
                          ...prev,
                          [`company_${signingDirector}`]: {
                            ...prev[`company_${signingDirector}`],
                            name: signingDirector, role: 'signing_director', email: a.email
                          }
                        }));
                        if (a.phone) setDirectorPhones(prev => ({ ...prev, [signingDirector]: a.phone }));
                        if (a.address) setDirectorAddresses(prev => ({ ...prev, [signingDirector]: prev[signingDirector] || a.address }));
                      }} />
                  </div>
                  
                  {/* Witness Toggle */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Witness Required</Label>
                    <RadioGroup 
                      value={signerConfig[`company_${signingDirector}`]?.needsWitness !== false ? "yes" : "no"}
                      onValueChange={(v) => setSignerConfig(prev => ({
                        ...prev,
                        [`company_${signingDirector}`]: {
                          ...prev[`company_${signingDirector}`],
                          name: signingDirector,
                          role: 'signing_director',
                          needsWitness: v === "yes",
                          witnessType: v === "yes" ? (prev[`company_${signingDirector}`]?.witnessType || 'signer_selects') : null
                        }
                      }))}
                      className="flex gap-4 mt-1"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id={`witness_company_yes`} />
                        <Label htmlFor={`witness_company_yes`} className="text-sm">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id={`witness_company_no`} />
                        <Label htmlFor={`witness_company_no`} className="text-sm">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
                
                {/* Witness Options */}
                {signerConfig[`company_${signingDirector}`]?.needsWitness !== false && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs text-muted-foreground mb-2 block">Witness Assignment</Label>
                    <RadioGroup 
                      value={signerConfig[`company_${signingDirector}`]?.witnessType || 'signer_selects'}
                      onValueChange={(v) => setSignerConfig(prev => ({
                        ...prev,
                        [`company_${signingDirector}`]: {
                          ...prev[`company_${signingDirector}`],
                          witnessType: v
                        }
                      }))}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="signer_selects" id={`witness_type_company_signer`} />
                        <Label htmlFor={`witness_type_company_signer`} className="text-sm">Signer Selects Own Witness</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="pre_assign" id={`witness_type_company_pre`} />
                        <Label htmlFor={`witness_type_company_pre`} className="text-sm">Pre-Assign Witness</Label>
                      </div>
                    </RadioGroup>
                    
                    {/* Pre-assign witness details */}
                    {signerConfig[`company_${signingDirector}`]?.witnessType === 'pre_assign' && (
                      <div className="mt-3">
                        <WitnessSelect
                          witnesses={companyWitnesses}
                          testId="witness-select-company"
                          onPick={(w) => setSignerConfig(prev => ({
                            ...prev,
                            [`company_${signingDirector}`]: {
                              ...prev[`company_${signingDirector}`],
                              witness: { name: w.name, email: w.email, address: w.address, occupation: w.occupation }
                            }
                          }))}
                        />
                      <div className="mt-1 grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Witness Name</Label>
                          <Input
                            placeholder="Full name..."
                            value={signerConfig[`company_${signingDirector}`]?.witness?.name || ""}
                            onChange={(e) => setSignerConfig(prev => ({
                              ...prev,
                              [`company_${signingDirector}`]: {
                                ...prev[`company_${signingDirector}`],
                                witness: { ...prev[`company_${signingDirector}`]?.witness, name: e.target.value }
                              }
                            }))}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Witness Email</Label>
                          <Input
                            type="email"
                            placeholder="Email..."
                            value={signerConfig[`company_${signingDirector}`]?.witness?.email || ""}
                            onChange={(e) => setSignerConfig(prev => ({
                              ...prev,
                              [`company_${signingDirector}`]: {
                                ...prev[`company_${signingDirector}`],
                                witness: { ...prev[`company_${signingDirector}`]?.witness, email: e.target.value }
                              }
                            }))}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Witness Address</Label>
                          <Input
                            placeholder="Address..."
                            value={signerConfig[`company_${signingDirector}`]?.witness?.address || ""}
                            onChange={(e) => setSignerConfig(prev => ({
                              ...prev,
                              [`company_${signingDirector}`]: {
                                ...prev[`company_${signingDirector}`],
                                witness: { ...prev[`company_${signingDirector}`]?.witness, address: e.target.value }
                              }
                            }))}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Witness Occupation</Label>
                          <Input
                            placeholder="Occupation..."
                            value={signerConfig[`company_${signingDirector}`]?.witness?.occupation || ""}
                            onChange={(e) => setSignerConfig(prev => ({
                              ...prev,
                              [`company_${signingDirector}`]: {
                                ...prev[`company_${signingDirector}`],
                                witness: { ...prev[`company_${signingDirector}`]?.witness, occupation: e.target.value }
                              }
                            }))}
                          />
                        </div>
                      </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Co-signing directors (positions 1, 2 — joint FTLA signatories) */}
            {signingDirectors.slice(1).map((coSignerName, coIdx) => {
              const dir = directors.find(d => d.name === coSignerName) || {};
              return (
                <div key={`cosigner-${coIdx}`} className="p-4 border rounded-lg bg-white space-y-3" data-testid={`cosigner-panel-${coIdx}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">{coSignerName}</span>
                      <Badge variant="secondary">Co-signer (Director)</Badge>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Email Address *</Label>
                    <Input
                      type="email"
                      placeholder="director@company.com"
                      value={signerConfig[`company_${coSignerName}`]?.email || directorEmails[coSignerName] || dir.email || ""}
                      onChange={(e) => setSignerConfig(prev => ({
                        ...prev,
                        [`company_${coSignerName}`]: {
                          ...prev[`company_${coSignerName}`],
                          name: coSignerName,
                          role: 'signing_director',
                          email: e.target.value
                        }
                      }))}
                      className="mt-1"
                      data-testid={`cosigner-email-${coIdx}`}
                    />
                    <SumsubUseButton name={coSignerName} testid={`sumsub-fill-cosigner-${coIdx}`}
                      onFill={(a) => {
                        if (a.email) setSignerConfig(prev => ({
                          ...prev,
                          [`company_${coSignerName}`]: {
                            ...prev[`company_${coSignerName}`],
                            name: coSignerName, role: 'signing_director', email: a.email
                          }
                        }));
                      }} />
                    <p className="text-xs text-muted-foreground mt-1">
                      Will receive a DocuSign envelope to sign the FTLA jointly.
                    </p>
                  </div>
                </div>
              );
            })}
            
            {/* Personal Guarantee Signers (directors + manual third-party PGs) */}
            {[...pgDirectors, ...manualPgGuarantors.map(pg => pg.name)].map((director, idx) => {
              const isAlsoSigningDirector = signingDirectors.includes(director);
              const useSameWitness = signerConfig[`pg_${director}`]?.useSameWitness;
              
              return (
              <div key={idx} className="p-4 border rounded-lg bg-white space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{director}</span>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      Personal Guarantor {idx + 1}
                    </Badge>
                    {isAlsoSigningDirector && (
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                        Also Signing Director
                      </Badge>
                    )}
                  </div>
                </div>
                {isAlsoSigningDirector && (
                  <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded px-2 py-1.5 -mt-1">
                    One DocuSign session — {director} signs BOTH as director and in their personal
                    capacity as guarantor in a single signing session (no duplicate emails/fields).
                  </p>
                )}
                
                {/* Email & Witness Toggle */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Email Address</Label>
                    <Input
                      type="email"
                      placeholder="Enter email..."
                      value={signerConfig[`pg_${director}`]?.email || 
                             directorEmails[director] ||
                             directors.find(d => d.name === director)?.email ||
                             manualPgGuarantors.find(p => p.name === director)?.email || ""}
                      onChange={(e) => setSignerConfig(prev => ({
                        ...prev,
                        [`pg_${director}`]: {
                          ...prev[`pg_${director}`],
                          name: director,
                          role: 'pg_signer',
                          email: e.target.value
                        }
                      }))}
                      data-testid={`pg-signer-email-${idx}`}
                    />
                    <SumsubUseButton name={director} testid={`sumsub-fill-pg-${idx}`}
                      onFill={(a) => {
                        if (a.email) setSignerConfig(prev => ({
                          ...prev,
                          [`pg_${director}`]: {
                            ...prev[`pg_${director}`],
                            name: director, role: 'pg_signer', email: a.email
                          }
                        }));
                        if (a.phone) setDirectorPhones(prev => ({ ...prev, [director]: a.phone }));
                        if (a.address) setDirectorAddresses(prev => ({ ...prev, [director]: prev[director] || a.address }));
                      }} />
                  </div>
                  
                  {/* Witness Toggle - Always Yes for PG */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Witness Required</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700 font-medium">Yes (Required for PG)</span>
                    </div>
                  </div>
                </div>
                
                {/* Use Same Witness Option - Only show if director is also signing director and has witness configured */}
                {isAlsoSigningDirector && signerConfig[`company_${signingDirector}`]?.needsWitness !== false && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`use_same_witness_${idx}`}
                        checked={useSameWitness || false}
                        onCheckedChange={(checked) => setSignerConfig(prev => ({
                          ...prev,
                          [`pg_${director}`]: {
                            ...prev[`pg_${director}`],
                            name: director,
                            role: 'pg_signer',
                            useSameWitness: checked,
                            witnessType: checked ? signerConfig[`company_${signingDirector}`]?.witnessType : (prev[`pg_${director}`]?.witnessType || 'signer_selects'),
                            witness: checked ? signerConfig[`company_${signingDirector}`]?.witness : prev[`pg_${director}`]?.witness
                          }
                        }))}
                      />
                      <Label htmlFor={`use_same_witness_${idx}`} className="text-sm text-blue-800 font-medium cursor-pointer">
                        Use same witness as Company Signing Director
                      </Label>
                    </div>
                    {useSameWitness && signerConfig[`company_${signingDirector}`]?.witnessType === 'pre_assign' && (
                      <p className="text-xs text-blue-600 mt-2 ml-6">
                        Using witness: {signerConfig[`company_${signingDirector}`]?.witness?.name || 'Not specified'}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Witness Options - Show only when NOT using same witness */}
                {!useSameWitness && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-muted-foreground mb-2 block">Witness Assignment</Label>
                  <RadioGroup 
                    value={signerConfig[`pg_${director}`]?.witnessType || 'signer_selects'}
                    onValueChange={(v) => setSignerConfig(prev => ({
                      ...prev,
                      [`pg_${director}`]: {
                        ...prev[`pg_${director}`],
                        name: director,
                        role: 'pg_signer',
                        needsWitness: true,
                        witnessType: v
                      }
                    }))}
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="signer_selects" id={`witness_type_pg_${idx}_signer`} />
                      <Label htmlFor={`witness_type_pg_${idx}_signer`} className="text-sm">Signer Selects Own Witness</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="pre_assign" id={`witness_type_pg_${idx}_pre`} />
                      <Label htmlFor={`witness_type_pg_${idx}_pre`} className="text-sm">Pre-Assign Witness</Label>
                    </div>
                  </RadioGroup>
                  
                  {/* Pre-assign witness details */}
                  {signerConfig[`pg_${director}`]?.witnessType === 'pre_assign' && (
                    <div className="mt-3">
                      <WitnessSelect
                        witnesses={companyWitnesses}
                        testId={`witness-select-pg-${idx}`}
                        onPick={(w) => setSignerConfig(prev => ({
                          ...prev,
                          [`pg_${director}`]: {
                            ...prev[`pg_${director}`],
                            witness: { name: w.name, email: w.email, address: w.address, occupation: w.occupation }
                          }
                        }))}
                      />
                    <div className="mt-1 grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Witness Name</Label>
                        <Input
                          placeholder="Full name..."
                          value={signerConfig[`pg_${director}`]?.witness?.name || ""}
                          onChange={(e) => setSignerConfig(prev => ({
                            ...prev,
                            [`pg_${director}`]: {
                              ...prev[`pg_${director}`],
                              witness: { ...prev[`pg_${director}`]?.witness, name: e.target.value }
                            }
                          }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Witness Email</Label>
                        <Input
                          type="email"
                          placeholder="Email..."
                          value={signerConfig[`pg_${director}`]?.witness?.email || ""}
                          onChange={(e) => setSignerConfig(prev => ({
                            ...prev,
                            [`pg_${director}`]: {
                              ...prev[`pg_${director}`],
                              witness: { ...prev[`pg_${director}`]?.witness, email: e.target.value }
                            }
                          }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Witness Address</Label>
                        <Input
                          placeholder="Address..."
                          value={signerConfig[`pg_${director}`]?.witness?.address || ""}
                          onChange={(e) => setSignerConfig(prev => ({
                            ...prev,
                            [`pg_${director}`]: {
                              ...prev[`pg_${director}`],
                              witness: { ...prev[`pg_${director}`]?.witness, address: e.target.value }
                            }
                          }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Witness Occupation</Label>
                        <Input
                          placeholder="Occupation..."
                          value={signerConfig[`pg_${director}`]?.witness?.occupation || ""}
                          onChange={(e) => setSignerConfig(prev => ({
                            ...prev,
                            [`pg_${director}`]: {
                              ...prev[`pg_${director}`],
                              witness: { ...prev[`pg_${director}`]?.witness, occupation: e.target.value }
                            }
                          }))}
                        />
                      </div>
                    </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            );
            })}
            
            {/* Cross Company Guarantee Signers — one signatory per XCG company */}
            {contractTypes.guaranteeType === "pg_cross_company" && crossCompanies.map((cc, idx) => {
              const key = `xcg_${cc.company_number}`;
              return (
              <div key={key} className="p-4 border rounded-lg bg-white space-y-3" data-testid={`xcg-signer-panel-${idx}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-amber-600" />
                    <span className="font-medium">{cc.signing_director_name}</span>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Cross Company Guarantor {idx + 1}
                    </Badge>
                    <Badge variant="secondary">{cc.company_name}</Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground -mt-1">
                  Signs the Cross Company Guarantee deed on behalf of {cc.company_name} ({cc.company_number}).
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Email Address *</Label>
                    <Input
                      type="email"
                      placeholder="Enter email..."
                      value={signerConfig[key]?.email || ""}
                      onChange={(e) => setSignerConfig(prev => ({
                        ...prev,
                        [key]: {
                          ...prev[key],
                          name: cc.signing_director_name,
                          role: 'cross_company_signer',
                          email: e.target.value
                        }
                      }))}
                      data-testid={`xcg-signer-email-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Witness Required?</Label>
                    <RadioGroup
                      value={signerConfig[key]?.needsWitness !== false ? "yes" : "no"}
                      onValueChange={(v) => setSignerConfig(prev => ({
                        ...prev,
                        [key]: { ...prev[key], needsWitness: v === "yes" }
                      }))}
                      className="flex gap-4 mt-2"
                      data-testid={`xcg-signer-witness-${idx}`}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id={`xcg_wit_yes_${idx}`} />
                        <Label htmlFor={`xcg_wit_yes_${idx}`} className="text-sm">Yes (deed)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id={`xcg_wit_no_${idx}`} />
                        <Label htmlFor={`xcg_wit_no_${idx}`} className="text-sm">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          
          {/* ====== SIMPLYFUNDED SIGNERS SECTION ====== */}
          <div className="space-y-4 mb-6">
            <h4 className="font-medium text-sm text-muted-foreground border-b pb-2">SimplyFunded Signers</h4>
            
            {/* SF Signer */}
            <div className="p-4 border rounded-lg bg-white space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">SimplyFunded Authorised Signatory</span>
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700">Lender</Badge>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => {
                    if (user) {
                      setSfSigner({
                        name: user.name || '',
                        email: user.email || '',
                        title: user.role === 'admin' ? 'Director' : user.role || 'Authorised Signatory'
                      });
                      toast.success("Filled with your details");
                    } else {
                      toast.error("Please log in to use this feature");
                    }
                  }}
                >
                  <UserCheck className="w-4 h-4 mr-1" />
                  That&apos;s Me
                </Button>
              </div>
              <p className="text-[11px] text-amber-700 mb-2">
                The signer chosen before generation is printed on the docs — changing it here only
                affects DocuSign routing. Regenerate the documents to update the printed signer.
              </p>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input
                    placeholder="e.g., Dean Morley"
                    value={sfSigner.name}
                    onChange={(e) => setSfSigner(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input
                    type="email"
                    placeholder="e.g., dean@simplyfunded.co.uk"
                    value={sfSigner.email}
                    onChange={(e) => setSfSigner(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Title</Label>
                  <Input
                    placeholder="e.g., Director"
                    value={sfSigner.title}
                    onChange={(e) => setSfSigner(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            
            {/* Witness Toggle - Only required for certain document types */}
            <div className="p-4 border rounded-lg bg-gray-50 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Require Witness Signatures?</span>
                  <p className="text-xs text-muted-foreground">
                    Standard FTLA + PG does not require witnesses. Enable for Debenture or specific requirements.
                  </p>
                </div>
                <RadioGroup
                  value={requireWitness ? "yes" : "no"}
                  onValueChange={(v) => setRequireWitness(v === "yes")}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="witness_req_no" />
                    <Label htmlFor="witness_req_no" className="text-sm">No (Standard)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="witness_req_yes" />
                    <Label htmlFor="witness_req_yes" className="text-sm">Yes (Required)</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
            
            {/* SF Witness - Only shown when witnesses are required */}
            {requireWitness && (
            <div className="p-4 border rounded-lg bg-white space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">SimplyFunded Witness</span>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Required</Badge>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => {
                    if (user) {
                      setSfWitness({
                        name: user.name || '',
                        email: user.email || '',
                        address: SF_DEFAULT_ADDRESS,
                        occupation: user.role === 'admin' ? 'Director' : 'Customer Services'
                      });
                      toast.success("Filled with your details & SF address");
                    } else {
                      toast.error("Please log in to use this feature");
                    }
                  }}
                >
                  <UserCheck className="w-4 h-4 mr-1" />
                  That&apos;s Me
                </Button>
              </div>
              
              <WitnessSelect
                witnesses={companyWitnesses}
                testId="witness-select-sf"
                onPick={(w) => setSfWitness({
                  name: w.name, email: w.email,
                  address: w.address || SF_DEFAULT_ADDRESS,
                  occupation: w.occupation,
                })}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input
                    placeholder="Full name..."
                    value={sfWitness.name}
                    onChange={(e) => setSfWitness(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input
                    type="email"
                    placeholder="Email..."
                    value={sfWitness.email}
                    onChange={(e) => setSfWitness(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Address</Label>
                  <Input
                    placeholder="Address..."
                    value={sfWitness.address}
                    onChange={(e) => setSfWitness(prev => ({ ...prev, address: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Occupation</Label>
                  <Input
                    placeholder="e.g., CSD"
                    value={sfWitness.occupation}
                    onChange={(e) => setSfWitness(prev => ({ ...prev, occupation: e.target.value }))}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Default SF Address: {SF_DEFAULT_ADDRESS}
              </p>
            </div>
            )}
          </div>
          
          {/* Send Button */}
          <div className="mt-6">
            <Button 
              onClick={sendToDocuSign}
              disabled={sendingToDocuSign}
              className="w-full bg-blue-600 hover:bg-blue-700"
              size="lg"
            >
              {sendingToDocuSign ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating DocuSign Envelope...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Open DocuSign to Place Signatures & Send
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              DocuSign will open where you can drag & drop signature fields
            </p>
          </div>
        </div>
      )}
      
      {/* DocuSign Success */}
      {docuSignResult && (
        <div className="border-t pt-6">
          <Card className={docuSignResult.sender_view_url ? "bg-blue-50 border-blue-200" : "bg-green-50 border-green-200"}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                {docuSignResult.sender_view_url ? (
                  <>
                    <FileText className="w-6 h-6 text-blue-600" />
                    <h4 className="font-semibold text-blue-800">DocuSign Envelope Created</h4>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <h4 className="font-semibold text-green-800">Envelope Ready</h4>
                  </>
                )}
              </div>
              
              <div className="space-y-2 text-sm">
                <p><strong>Envelope ID:</strong> {docuSignResult.envelope_id}</p>
                <p><strong>Status:</strong> {docuSignResult.status === "created" ? "Draft (Pending Signature Placement)" : docuSignResult.status}</p>
                <p><strong>Documents:</strong> {docuSignResult.document_count}</p>
                <p><strong>Signers:</strong> {docuSignResult.signer_count}</p>
              </div>
              
              {docuSignResult.sender_view_url && !showDocuSignIframe && (
                <div className="mt-4">
                  <Button 
                    onClick={() => {
                      setDocuSignIframeUrl(docuSignResult.sender_view_url);
                      setShowDocuSignIframe(true);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Open DocuSign to Place Signatures
                  </Button>
                  <p className="text-xs text-blue-600 mt-2 text-center">
                    Click above to open DocuSign&apos;s tagging interface. Place signature fields on each document, then click &quot;Send&quot; in DocuSign.
                  </p>
                </div>
              )}
              
              {docuSignResult.is_mock && (
                <div className="mt-3 p-2 bg-amber-100 border border-amber-200 rounded text-xs text-amber-800">
                  ⚠️ Mock Mode: DocuSign API not configured. In production, this would open DocuSign&apos;s tagging interface where you can drag and drop signature fields onto the documents before sending.
                </div>
              )}
            </CardContent>
          </Card>
          
          <div className="mt-4 text-center">
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Close & Return to Documents
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Calculate next step
  const getNextStep = () => {
    if (currentStep === 1) return 2;
    if (currentStep === 2) return 3;
    if (currentStep === 3) return 4; // Always go to bank details
    if (currentStep === 4) {
      // After bank details, check if cross company is needed
      return contractTypes.guaranteeType === "pg_cross_company" ? 5 : 6;
    }
    if (currentStep === 5) return 6;
    return currentStep;
  };

  // Check if can proceed to next step
  const canProceed = () => {
    if (currentStep === 1) return true;
    if (currentStep === 2) return selectedCompany && companyDetails;
    if (currentStep === 3) {
      // Need signing director AND at least one PG (from directors OR manual)
      const totalPGs = pgDirectors.length + manualPgGuarantors.length;
      return signingDirector && totalPGs > 0;
    }
    if (currentStep === 4) {
      // Bank details must be CoP verified OR already verified from Stage 2b
      return bankDetails.verified || bankDetailsSource === "stage_2b" || bankDetailsSource === "credit_approval";
    }
    if (currentStep === 5) {
      if (contractTypes.guaranteeType !== "pg_cross_company") return true;
      // Require at least one cross company to be added
      return crossCompanies.length > 0;
    }
    return true;
  };

  return (
    <div className={wizardOnly ? "" : "p-6 max-w-7xl mx-auto"}>
      {/* Header */}
      {!wizardOnly && (
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Onboarding Centre</h1>
          <p className="text-muted-foreground">
            {onboardingMode === "broker"
              ? "Generate & send Broker Engagement Letters"
              : "End-to-end customer onboarding — agreements, e-signature & direct debit"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Mode toggle — Customer vs Broker */}
          <div className="inline-flex rounded-lg border bg-slate-50 p-1" data-testid="onboarding-mode-toggle">
            <button
              type="button"
              onClick={() => setOnboardingMode("customer")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                onboardingMode === "customer"
                  ? "bg-white shadow-sm text-blue-700"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="onboarding-mode-customer"
            >
              Customers
            </button>
            <button
              type="button"
              onClick={() => setOnboardingMode("broker")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                onboardingMode === "broker"
                  ? "bg-white shadow-sm text-blue-700"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="onboarding-mode-broker"
            >
              Brokers
            </button>
          </div>
          {onboardingMode === "customer" && (
            <Button onClick={() => startGeneration(null)}>
              <Plus className="w-4 h-4 mr-2" />
              New Onboarding
            </Button>
          )}
        </div>
      </div>
      )}

      {/* Broker Onboarding section — renders its own dashboard + wizard */}
      {!wizardOnly && onboardingMode === "broker" && <BrokerOnboarding />}

      {/* Customer Onboarding section — existing wizard + dashboard */}
      {(wizardOnly || onboardingMode === "customer") && (
        <>
      {!wizardOnly && (<>
      {/* Main Split View - Funding Docs / Legal Docs */}
      <Tabs defaultValue="funding" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="funding" className="flex items-center gap-2 py-3">
            <FileText className="w-4 h-4" />
            Funding Docs
            {(requests.length + inProgressContracts.filter(c => c.application_id?.startsWith("SF-") && !c.application_id?.startsWith("MANUAL")).length) > 0 && (
              <Badge variant="secondary" className="bg-red-100 text-red-700">
                {requests.length + inProgressContracts.filter(c => c.application_id?.startsWith("SF-") && !c.application_id?.startsWith("MANUAL")).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="legal" className="flex items-center gap-2 py-3">
            <Building2 className="w-4 h-4" />
            Legal Docs
            {completedContracts.legal_tab.length > 0 && (
              <Badge variant="secondary">{completedContracts.legal_tab.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        {/* ========== FUNDING DOCS TAB (FTLA, Deb, PG) ========== */}
        <TabsContent value="funding" className="space-y-6">
          {/* Docs Out — Monday deals already sent for signing */}
          <DocsOutTrackingSection />

          {/* Pending Requests from Credit Approved */}
          {requests.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-red-600 animate-pulse" />
                  <CardTitle className="text-lg text-red-800">Pending Requests ({requests.length})</CardTitle>
                </div>
                <p className="text-sm text-red-700">From Credit Approved Applications - awaiting document generation</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {requests.map((request, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border"
                    >
                      <div>
                        <p className="font-medium">{request.company_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {request.application_id} • £{request.loan_amount?.toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => markRequestDone(request.application_id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Done
                        </Button>
                        <Button size="sm" onClick={() => startGeneration(request)}>
                          Generate Docs
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* In Progress - Funding Docs */}
          {inProgressContracts.filter(c => c.application_id?.startsWith("SF-") && !c.application_id?.startsWith("MANUAL")).length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <Clock className="w-5 h-5" />
                  In Progress - Awaiting Signature Setup
                  <Badge variant="secondary" className="ml-2 bg-amber-200 text-amber-800">
                    {inProgressContracts.filter(c => c.application_id?.startsWith("SF-") && !c.application_id?.startsWith("MANUAL")).length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {inProgressContracts
                    .filter(c => c.application_id?.startsWith("SF-") && !c.application_id?.startsWith("MANUAL"))
                    .map((contract, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-amber-600" />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{contract.company_name}</p>
                            <div className="flex gap-1">
                              {getDocumentTypeLabels(contract).map((label, i) => (
                                <Badge key={i} variant="outline" className="text-xs px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {contract.application_id} • Envelope: {contract.envelope_id?.substring(0, 8)}...
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => discardEnvelope(contract.envelope_id, contract.company_name)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Discard
                        </Button>
                        <Button size="sm" onClick={() => resumeEnvelope(contract.envelope_id)}>
                          <ChevronRight className="w-4 h-4 mr-1" />
                          Resume
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
        </TabsContent>
        
        {/* ========== LEGAL DOCS TAB (Letters, Notices, etc.) ========== */}
        <TabsContent value="legal" className="space-y-6">
          {/* In Progress - Manual/Other */}
          {inProgressContracts.filter(c => !c.application_id?.startsWith("SF-") || c.application_id?.startsWith("MANUAL")).length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <Clock className="w-5 h-5" />
                  In Progress
                  <Badge variant="secondary" className="ml-2 bg-amber-200 text-amber-800">
                    {inProgressContracts.filter(c => !c.application_id?.startsWith("SF-") || c.application_id?.startsWith("MANUAL")).length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {inProgressContracts
                    .filter(c => !c.application_id?.startsWith("SF-") || c.application_id?.startsWith("MANUAL"))
                    .map((contract, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-amber-600" />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{contract.company_name}</p>
                            <div className="flex gap-1">
                              {getDocumentTypeLabels(contract).map((label, i) => (
                                <Badge key={i} variant="outline" className="text-xs px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {contract.application_id} • Envelope: {contract.envelope_id?.substring(0, 8)}...
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => discardEnvelope(contract.envelope_id, contract.company_name)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Discard
                        </Button>
                        <Button size="sm" onClick={() => resumeEnvelope(contract.envelope_id)}>
                          <ChevronRight className="w-4 h-4 mr-1" />
                          Resume
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Completed Legal Docs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Completed Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingCompleted ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Loading...
                </div>
              ) : completedContracts.contracts_tab.length > 0 ? (
                <div className="space-y-3">
                  {completedContracts.contracts_tab.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg border-green-200 bg-green-50">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium">{item.company_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.application_id} • Sent: {new Date(item.sent_at || item.created_at).toLocaleDateString("en-GB")}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        {item.status || 'sent'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No legal documents yet</p>
                  <p className="text-sm mt-2">Letters before action, default notices, etc. will appear here</p>
                  <Button variant="outline" className="mt-4" onClick={() => startGeneration(null)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Legal Document
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* DocuSign Envelopes — same panel as the AUS Onboarding Centre */}
      <DocuSignEnvelopesPanel
        envelopes={allEnvelopes}
        loading={loadingEnvelopes}
        error={envelopesError}
        onRefresh={loadDocuSignEnvelopes}
        locale="en-GB"
      />
      </>)}

      {/* Generation Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={(open) => {
        if (!open && wizardOnly) { setShowGenerateDialog(false); onWizardClose?.(); return; }
        if (!open) {
          // Reset iframe state when closing
          setShowDocuSignIframe(false);
          setDocuSignIframeUrl(null);
        }
        setShowGenerateDialog(open);
      }}>
        {/* DocuSign Iframe View */}
        {showDocuSignIframe && docuSignIframeUrl ? (
          <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] p-0" aria-describedby={undefined}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b bg-blue-50">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-blue-900">DocuSign - Place Signature Fields</h3>
                    <p className="text-sm text-blue-700">
                      Drag and drop signature fields onto the documents, then click Send in DocuSign
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <WizardDebugButton />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowDocuSignIframe(false);
                      setDocuSignIframeUrl(null);
                    }}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Close & Return
                  </Button>
                </div>
              </div>
              
              {/* DocuSign iframe */}
              <div className="flex-1 bg-gray-100">
                <iframe
                  src={docuSignIframeUrl}
                  className="w-full h-full border-0"
                  title="DocuSign Sender View"
                  allow="camera; microphone"
                />
              </div>
              
              {/* Footer with actions */}
              <div className="p-3 border-t bg-gray-50 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Envelope ID: {docuSignResult?.envelope_id} • 
                  {docuSignResult?.document_count} document(s) • 
                  {docuSignResult?.signer_count} signer(s)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowDocuSignIframe(false);
                      setDocuSignIframeUrl(null);
                      toast.info("Progress saved - you can resume later from 'In Progress' section");
                      loadInProgressContracts();
                    }}
                  >
                    Save & Exit
                  </Button>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleDocuSignComplete(docuSignResult?.envelope_id)}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    I&apos;ve Sent in DocuSign
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        ) : (
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Documents</DialogTitle>
            <DialogDescription>
              {selectedRequest 
                ? `Generating documents for ${selectedRequest.company_name}`
                : "Create new documents from scratch"
              }
            </DialogDescription>
            {(mondayItemId || offerPdfUrl) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="absolute right-10 top-3 h-7 text-xs"
                onClick={openOfferView}
                data-testid="view-offer-btn"
              >
                <Eye className="w-3 h-3 mr-1" /> View Offer
              </Button>
            )}
            <button
              type="button"
              onClick={() => {
                if (wizardOnly) { setShowGenerateDialog(false); onWizardClose?.(); }
                else navigate("/credit-deals");
              }}
              className="text-xs text-blue-700 hover:underline flex items-center gap-1 mt-1 w-fit"
              data-testid="back-to-credit-deals-link"
            >
              ← Back to Credit Approved Deals
            </button>
          </DialogHeader>
          
          {/* Progress Steps - 6 steps now */}
          <div className="flex items-center justify-between mb-6 px-4">
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${currentStep === step ? 'bg-blue-600 text-white' : 
                    currentStep > step ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}
                `}>
                  {currentStep > step ? <CheckCircle className="w-4 h-4" /> : step}
                </div>
                {step < 6 && (
                  <div className={`w-8 h-0.5 ${currentStep > step ? 'bg-green-600' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          
          {/* Step Labels */}
          <div className="flex justify-between text-xs text-muted-foreground mb-4 px-1">
            <span>Contracts</span>
            <span>Company</span>
            <span>Directors</span>
            <span>Bank</span>
            <span>Cross Co.</span>
            <span>Generate</span>
          </div>
          
          {/* Step Content */}
          <div className="min-h-[300px]">
            {renderStepContent()}
          </div>
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
              disabled={currentStep === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            
            {currentStep < 6 ? (
              <Button 
                onClick={() => {
                  const nextStep = getNextStep();
                  if (nextStep === 6) {
                    // Generate all documents at the end
                    generateDocuments();
                  } else {
                    setCurrentStep(nextStep);
                  }
                }}
                disabled={!canProceed() || generating}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : getNextStep() === 6 ? (
                  <>Generate All Documents</>
                ) : (
                  <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            ) : (
              <Button onClick={() => setShowGenerateDialog(false)}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
        )}
      </Dialog>

      {/* Offer PDF viewer — quick reference at any wizard step */}
      <Dialog open={!!offerViewUrl} onOpenChange={(o) => {
        if (!o) {
          if (offerViewUrl?.blob) URL.revokeObjectURL(offerViewUrl.url);
          setOfferViewUrl(null);
        }
      }}>
        <DialogContent className="max-w-4xl h-[85vh] p-2 flex flex-col" aria-describedby={undefined} data-testid="offer-view-dialog">
          <DialogHeader className="px-2 pt-1 pb-0">
            <DialogTitle className="text-sm">Offer Proposal</DialogTitle>
          </DialogHeader>
          {offerViewUrl?.loading ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading offer…
            </div>
          ) : (
            <iframe src={offerViewUrl?.url || ""} title="Offer PDF" className="w-full flex-1 border-0 rounded" />
          )}
        </DialogContent>
      </Dialog>

      {/* Generated-document preview (view without downloading) */}
      <Dialog open={!!docPreview} onOpenChange={(o) => {
        if (!o) {
          if (docPreview?.blob) URL.revokeObjectURL(docPreview.url);
          setDocPreview(null);
        }
      }}>
        <DialogContent className="max-w-4xl h-[85vh] p-2 flex flex-col" aria-describedby={undefined} data-testid="doc-preview-dialog">
          <DialogHeader className="px-2 pt-1 pb-0">
            <DialogTitle className="text-sm">{docPreview?.name || "Document"}</DialogTitle>
          </DialogHeader>
          <iframe src={docPreview?.url || ""} title="Document preview" className="w-full flex-1 border-0 rounded" />
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}

